import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Helmet } from 'react-helmet';
import { useApolloClient, useLazyQuery } from '@apollo/client';
import {
  Alert,
  Grid,
  CircularProgress,
  Button,
  IconButton,
  Tooltip,
  Divider,
  InputLabel,
  Typography,
  MenuItem,
  Select,
  Box,
  TextField,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';

import { useQuery, useMutation, useSubscription } from '@apollo/client';
import i18next from 'i18next';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import ErrorIcon from '@mui/icons-material/ErrorOutline';
import WarningIcon from '@mui/icons-material/WarningAmber';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PeopleIcon from '@mui/icons-material/People';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import InfoIcon from '@mui/icons-material/Info';
import WbSunnyOutlinedIcon from '@mui/icons-material/WbSunnyOutlined';
import AcUnitOutlinedIcon from '@mui/icons-material/AcUnitOutlined';

import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import yup from 'validation';

import { dispatchException, dispatchMessage } from 'helper/snackbar';
import { slugify } from 'semshared/utils/format';
import { userSelector, canEditAdminRecord, canEditAdminSpaceId, initialSpaceId, filterForSelectableRecords } from 'helper/security';
import { getFullBackendUrl } from 'helper/download';

import { FormInputText } from 'components/form/FormInputText';
import { FormInputInteger } from 'components/form/FormInputInteger';
import { FormInputDecimal } from 'components/form/FormInputDecimal';
import { FormInputNumber } from 'components/form/FormInputNumber';
import {
  FormInputCountryDropdown,
  FormInputDropdown,
  FormInputLanguageDropdown,
  FormInputSubdivisionDropdown,
  SimpleDropdown,
} from 'components/form/FormInputDropdown';
import { FormInputMultiCheckbox } from 'components/form/FormInputMultiCheckbox';
import { FormInputRichText } from 'components/form/FormInputRichTextEditor';
import { UnsavedChangesPrompt } from 'components/form/UnsavedChangesPrompt';
import ConfirmationButton from 'components/dialogs/ConfirmationButton';

import {
  HOTEL_VIEW_QUERY,
  UPDATE_HOTEL_MUTATION,
  UPDATE_HOTEL_GROUPS_MUTATION,
  CREATE_HOTEL_MUTATION,
  DELETE_HOTEL_MUTATION,
  REFETCH_HOTELS_QUERIES,
  EVICT_HOTELS_QUERIES,
  COORDINATES_BY_ADDRESS_QUERY,
  HOTEL_GROUPS_LIST_QUERY,
  HOTEL_CHANGED_SUBSCRIPTION,
  UPLOAD_HOTEL_AGB_MUTATION,
  GET_WORDPRESS_URL_QUERY,
} from '../gql';
import { LIST_HOTEL_GROUPS_QUERY } from '../../groups/gql';
import { OFFERTEMPLATES_LIST_QUERY, UPLOAD_CONTENTMEDIA_MUTATION, DELETE_CONTENTMEDIA_MUTATION, EVICT_CONTENTMEDIA_QUERIES } from '../../content/gql';
import { SERVICETYPES_LIST_QUERY } from '../../products/gql';

import {
  HotelListOutput,
  EHotelWidgetStatusType,
  HotelMeetingRoomInput,
  EContentHotelAttribute,
  EContentHotelTextType,
  ListServiceTypesQuery,
  ViewHotelQuery,
  ListOfferTemplatesQuery,
  ECountryCode,
  ELanguageCode,
  EHotelListerStatusType,
  EClientGender,
  EContentHotelListerServiceTypeAssignment,
  EContentHotelContactType,
  EContentEmailTemplate,
  EContentMediaArea,
} from '__generated__/graphql';
import CustomTabs from 'components/Tabs';
import ContractsTab from '../../contracts/ContractsTab';
import { FormInputCheckbox } from 'components/form/FormInputCheckbox';
import { FormColorPicker } from 'components/form/FormInputColorPicker';
import { SpaceSelectionInput } from 'components/security/SpaceSelectionInput';
import {
  buildGroupedSpaceSelectionOptions,
  GroupedSpaceMultiCheckboxInput,
  GroupedSpaceSelectionInput,
} from 'components/security/GroupedSpaceSelectionInput';
import { filterSelector, bookingModeSelector } from 'helper/filter';
import SimpleTable from 'components/table/SimpleTable';
import SortableMediaTable from 'components/table/SortableMediaTable';
import FileUploadButton from 'components/FileUploadButton';
import SimpleDialog from 'components/dialogs/Dialog';
import { RedirectError } from 'pages/error';
import { FormInputPercentage } from 'components/form/FormInputPercentage';
import { formatDocumentTitle } from 'helper/usedocumenttitle';
import { isProductionRelease } from 'helper/deployment';
import { listDefaultClientCancellationRules, listPremiumClientCancellationRules } from 'semshared/offer/cancellation';
import { formatPercentage } from 'components/Percentage';
import { FormInputRadio } from 'components/form/FormInputRadio';
import CustomIcon, { IconName } from 'components/ui/CustomIcon';
import { EBookingMode } from 'types';
import HotelCommissionTab from './HotelCommissionTab';
import HotelMeetingPackageTab from './HotelMeetingPackageTab';
import HotelTasksList from '../../../components/HotelTasksList';
import TasksIcon from '@mui/icons-material/Task';
import HotelCommentsSection from 'components/comments/HotelCommentsSection';
import { HotelStandardDocuments } from 'components/HotelStandardDocuments';

const REMINDER_TYPES: EContentEmailTemplate[] = [
  EContentEmailTemplate.LISTER_NONBINDING_REQUEST_HOTEL,
  EContentEmailTemplate.LISTER_REMINDER_REQUEST_HOTEL,
  EContentEmailTemplate.LISTER_REJECTED_OFFER_HOTEL,
  EContentEmailTemplate.LISTER_RESERVATION_HOTEL,
  EContentEmailTemplate.LISTER_CR_HOTEL,
  EContentEmailTemplate.CLASSIC_CR_HOTEL,
  EContentEmailTemplate.LISTER_TITLE_CHANGE_HOTEL,
  EContentEmailTemplate.LISTER_BOOKING_INFORMATION_HOTEL,
  EContentEmailTemplate.LISTER_BOOKING_REMINDER_START_SOON_HOTEL,
  EContentEmailTemplate.LISTER_BOOKING_REMINDER_END_TODAY_HOTEL,
  EContentEmailTemplate.LISTER_BOOKING_CANCELLATION_HOTEL,
  EContentEmailTemplate.LISTER_AVAILABILITY_APPROVED_CLIENT,
  EContentEmailTemplate.LISTER_RESERVATION_APPROVED_CLIENT,
  EContentEmailTemplate.LISTER_INVOICE_HOTEL,
  EContentEmailTemplate.LISTER_CREDIT_NOTE_HOTEL,
];

interface ValidationSummary {
  productsWithoutBasePrice: number;
  productsWithoutBasePriceNames: string[];
  productsWithoutPriceComponents: number;
  productsWithoutPriceComponentsNames: string[];
  bundlesWithoutBasePrice: number;
  bundlesWithoutBasePriceNames: string[];
  bundlesWithoutPriceComponents: number;
  bundlesWithoutPriceComponentsNames: string[];
  facilitiesWithoutBasePrice: number;
  facilitiesWithoutBasePriceNames: string[];
  facilitiesWithoutPriceComponents: number;
  facilitiesWithoutPriceComponentsNames: string[];
  // Tax validation
  facilitiesWithoutTaxRates?: number;
  facilitiesWithoutTaxRatesNames?: string[];
  productsWithoutTaxRates?: number;
  productsWithoutTaxRatesNames?: string[];
  bundlesWithoutTaxRates?: number;
  bundlesWithoutTaxRatesNames?: string[];
  serviceTypesIncomplete?: Array<{
    serviceTypeName: string;
    missingProducts: string[];
    missingFacilities: string[];
    invalidProducts: string[];
    invalidFacilities: string[];
  }> | null;
}

const formatValidationDetails = (validation: ValidationSummary | null | undefined): React.ReactNode[] => {
  if (!validation) return [];

  const details: React.ReactNode[] = [];

  if (validation.productsWithoutBasePrice > 0) {
    details.push(
      <div key="products-without-base">
        <strong>{i18next.t('pricelist-validation-products-without-base-price')}:</strong>
        <ul style={{ marginTop: '4px', marginBottom: '4px', paddingLeft: '20px' }}>
          {validation.productsWithoutBasePriceNames.map((name, idx) => (
            <li key={idx}>{name}</li>
          ))}
        </ul>
      </div>,
    );
  }

  if (validation.productsWithoutPriceComponents > 0) {
    details.push(
      <div key="products-without-components">
        <strong>{i18next.t('pricelist-validation-products-without-price-components')}:</strong>
        <ul style={{ marginTop: '4px', marginBottom: '4px', paddingLeft: '20px' }}>
          {validation.productsWithoutPriceComponentsNames.map((name, idx) => (
            <li key={idx}>{name}</li>
          ))}
        </ul>
      </div>,
    );
  }

  if (validation.bundlesWithoutBasePrice > 0) {
    details.push(
      <div key="bundles-without-base">
        <strong>{i18next.t('pricelist-validation-bundles-without-base-price')}:</strong>
        <ul style={{ marginTop: '4px', marginBottom: '4px', paddingLeft: '20px' }}>
          {validation.bundlesWithoutBasePriceNames.map((name, idx) => (
            <li key={idx}>{name}</li>
          ))}
        </ul>
      </div>,
    );
  }

  if (validation.bundlesWithoutPriceComponents > 0) {
    details.push(
      <div key="bundles-without-components">
        <strong>{i18next.t('pricelist-validation-bundles-without-price-components')}:</strong>
        <ul style={{ marginTop: '4px', marginBottom: '4px', paddingLeft: '20px' }}>
          {validation.bundlesWithoutPriceComponentsNames.map((name, idx) => (
            <li key={idx}>{name}</li>
          ))}
        </ul>
      </div>,
    );
  }

  if (validation.facilitiesWithoutBasePrice > 0) {
    details.push(
      <div key="facilities-without-base">
        <strong>{i18next.t('pricelist-validation-facilities-without-base-price')}:</strong>
        <ul style={{ marginTop: '4px', marginBottom: '4px', paddingLeft: '20px' }}>
          {validation.facilitiesWithoutBasePriceNames.map((name, idx) => (
            <li key={idx}>{name}</li>
          ))}
        </ul>
      </div>,
    );
  }

  if (validation.facilitiesWithoutPriceComponents > 0) {
    details.push(
      <div key="facilities-without-components">
        <strong>{i18next.t('pricelist-validation-facilities-without-price-components')}:</strong>
        <ul style={{ marginTop: '4px', marginBottom: '4px', paddingLeft: '20px' }}>
          {validation.facilitiesWithoutPriceComponentsNames.map((name, idx) => (
            <li key={idx}>{name}</li>
          ))}
        </ul>
      </div>,
    );
  }

  // Tax validation - items without tax rates for hotel's country
  if (validation.facilitiesWithoutTaxRates && validation.facilitiesWithoutTaxRates > 0) {
    details.push(
      <div key="facilities-tax">
        <strong>{i18next.t('validation-facilities-without-tax-rates')}:</strong>
        <ul style={{ marginTop: '4px', marginBottom: '4px', paddingLeft: '20px' }}>
          {(validation.facilitiesWithoutTaxRatesNames || []).map((name, idx) => (
            <li key={idx}>{name}</li>
          ))}
        </ul>
      </div>,
    );
  }

  if (validation.productsWithoutTaxRates && validation.productsWithoutTaxRates > 0) {
    details.push(
      <div key="products-tax">
        <strong>{i18next.t('validation-products-without-tax-rates')}:</strong>
        <ul style={{ marginTop: '4px', marginBottom: '4px', paddingLeft: '20px' }}>
          {(validation.productsWithoutTaxRatesNames || []).map((name, idx) => (
            <li key={idx}>{name}</li>
          ))}
        </ul>
      </div>,
    );
  }

  if (validation.bundlesWithoutTaxRates && validation.bundlesWithoutTaxRates > 0) {
    details.push(
      <div key="bundles-tax">
        <strong>{i18next.t('validation-bundles-without-tax-rates')}:</strong>
        <ul style={{ marginTop: '4px', marginBottom: '4px', paddingLeft: '20px' }}>
          {(validation.bundlesWithoutTaxRatesNames || []).map((name, idx) => (
            <li key={idx}>{name}</li>
          ))}
        </ul>
      </div>,
    );
  }

  // Add service type validation details
  if (validation.serviceTypesIncomplete && validation.serviceTypesIncomplete.length > 0) {
    validation.serviceTypesIncomplete.forEach((st, stIdx) => {
      const stDetails: React.ReactNode[] = [];

      if (st.missingProducts.length > 0) {
        stDetails.push(
          <div key={`${stIdx}-missing-products`}>
            {i18next.t('validation-missing-products', { defaultValue: 'Fehlende Produkte' })}:
            <ul style={{ marginTop: '4px', marginBottom: '4px', paddingLeft: '20px' }}>
              {st.missingProducts.map((name, idx) => (
                <li key={idx}>{name}</li>
              ))}
            </ul>
          </div>,
        );
      }

      if (st.missingFacilities.length > 0) {
        stDetails.push(
          <div key={`${stIdx}-missing-facilities`}>
            {i18next.t('validation-missing-services', { defaultValue: 'Fehlende Leistungen' })}:
            <ul style={{ marginTop: '4px', marginBottom: '4px', paddingLeft: '20px' }}>
              {st.missingFacilities.map((name, idx) => (
                <li key={idx}>{name}</li>
              ))}
            </ul>
          </div>,
        );
      }

      if (st.invalidProducts.length > 0) {
        stDetails.push(
          <div key={`${stIdx}-invalid-products`}>
            {i18next.t('validation-invalid-products', { defaultValue: 'Ungültige Produkte' })}:
            <ul style={{ marginTop: '4px', marginBottom: '4px', paddingLeft: '20px' }}>
              {st.invalidProducts.map((name, idx) => (
                <li key={idx}>{name}</li>
              ))}
            </ul>
          </div>,
        );
      }

      if (st.invalidFacilities.length > 0) {
        stDetails.push(
          <div key={`${stIdx}-invalid-facilities`}>
            {i18next.t('validation-invalid-services', { defaultValue: 'Ungültige Leistungen' })}:
            <ul style={{ marginTop: '4px', marginBottom: '4px', paddingLeft: '20px' }}>
              {st.invalidFacilities.map((name, idx) => (
                <li key={idx}>{name}</li>
              ))}
            </ul>
          </div>,
        );
      }

      if (stDetails.length > 0) {
        details.push(
          <div key={`servicetype-${stIdx}`}>
            <strong>{st.serviceTypeName}:</strong>
            <div style={{ paddingLeft: '12px' }}>{stDetails}</div>
          </div>,
        );
      }
    });
  }

  return details;
};

interface HotelProps {
  id: number;
}
interface HotelCreateProps { }
interface HotelFormProps {
  data: NonNullable<ViewHotelQuery['viewHotel']>;
  serviceTypes: ListServiceTypesQuery['listServiceTypes']['serviceTypes'];
  offerTemplates: ListOfferTemplatesQuery['listOfferTemplates'];
  refetch: () => void;
}

const validationSchema = yup.object().shape({
  isCreate: yup.boolean().required(),
  billing: yup.object().shape({
    uid: yup.string().nullable(),
    iban: yup.string().nullable(),
    bic: yup.string().nullable(),
  }),
  business: yup.object().shape({
    name: yup.string().required().label(i18next.t('hotel-name')),
    spaceId: yup.number().required().label(i18next.t('field-space')),
    refCode: yup.string(),
    extRefCode: yup.string().nullable(),
    businessName: yup.string().nullable(),
    businessAddress1: yup.string().required().label(i18next.t('hotel-businessaddress1')),
    businessAddress2: yup.string().nullable(),
    businessAddress3: yup.string().nullable(),
    businessCountry: yup.mixed<ECountryCode>().oneOf(Object.values(ECountryCode)).required().label(i18next.t('hotel-businesscountry')),
    businessZip: yup.string().required().label(i18next.t('hotel-businesszip')),
    businessCity: yup.string().required().label(i18next.t('hotel-businesscity')),
    businessState: yup.string().nullable(),
    businessPhone: yup.string().nullable(),
    businessUrl: yup.string().nullable(),
    businessEmail: yup.string().nullable(),
  }),
  differentInvoiceAddress: yup.boolean().default(false),
  invoice: yup
    .object()
    .shape({
      address1: yup.string().notRequired(),
      address2: yup.string().notRequired(),
      address4: yup.string().notRequired(),
      country: yup.mixed<ECountryCode>().oneOf(Object.values(ECountryCode)).notRequired().label(i18next.t('hotel-businesscountry')),
      zip: yup.string().notRequired().label(i18next.t('hotel-businesszip')),
      city: yup.string().notRequired().label(i18next.t('hotel-businesscity')),
      state: yup.string().notRequired(),
    })
    .notRequired(),
  notifications: yup.object().shape({
    notificationEmail: yup.string().email().required().label(i18next.t('hotel-notificationemail')),
    offerNotApprovedReminderHotel: yup.number().nullable(),
    offerNotApprovedReminderClient: yup.number().nullable(),
    depositReminderHotel: yup.number().nullable(),
    depositReminderCheckHotel: yup.number().nullable(),
    reminders: yup
      .array()
      .of(
        yup.object().shape({
          template: yup.mixed<EContentEmailTemplate>().required(),
          receivers: yup.array().of(yup.string().required().email()).required(),
        }),
      )
      .required(),
  }),
  widget: yup.object().shape({
    showInWidget: yup.boolean().required(),
    widgetUrl: yup.string().nullable(),
    widgetColor: yup.string().required(),
    widgetColorEnabled: yup.boolean().required(),
    widgetPrivacyUrl: yup.string().nullable(),
    widgetTermsUrl: yup.string().nullable(),
    widgetNoRooms: yup.boolean(),
    widgetExpandedMode: yup.boolean(),
    widgetSlimMode: yup.boolean(),
    offerTemplateId: yup.number(),
    offerExpirationDays: yup.number().nullable(),
    widgetServiceTypeIds: yup.array().required().of(yup.number().required()),
    enableAvailabilityCheck: yup.boolean(),
  }),
  lister: yup.object().shape({
    showInLister: yup.boolean().required(),
    slug: yup.string(),
    listerServiceTypeIdSmall: yup.number().nullable(),
    listerServiceTypeIdMedium: yup.number().nullable(),
    listerServiceTypeIdRegular: yup.number().nullable(),
    locationLatitude: yup
      .number()
      .transform((value, originalValue) => (originalValue === '' ? undefined : value))
      .nullable(),
    locationLongitude: yup
      .number()
      .transform((value, originalValue) => (originalValue === '' ? undefined : value))
      .nullable(),
    rating: yup.number().nullable().label(i18next.t('hotel-rating')),
    meetingRooms: yup.array().of(
      yup.object().shape({
        id: yup.number().nullable(),
        name: yup.string().required(),
        area: yup.number().required(),
        capacityUForm: yup.number().nullable(),
        capacityTheater: yup.number().nullable(),
        capacityParlament: yup.number().nullable(),
        capacityCircle: yup.number().nullable(),
        capacityBankett: yup.number().nullable(),
        capacityCocktail: yup.number().nullable(),
        capacityBlock: yup.number().nullable(),
        daylight: yup.boolean().nullable(),
        airConditioning: yup.boolean().nullable(),
        facilityId: yup.number().nullable(),
      }),
    ),
    medias: yup.array().of(
      yup.object().shape({
        id: yup.number().required(),
        name: yup.string().required(),
        previewUrl: yup.string().required(),
        description: yup.string().nullable().max(60),
      }),
    ),
    stars: yup.string().nullable(),
    attributes: yup.array().of(yup.string().required()),
    cancellationToleranceRate: yup
      .number()
      .nullable()
      .transform((value, originalValue) => (originalValue === '' || originalValue === null || originalValue === undefined ? null : value))
      .min(0)
      .max(100),
    cancellationRules: yup.array().of(
      yup.object().shape({
        daysToEvent: yup.number().required(),
        minCapacity: yup.number().nullable(),
        maxCapacity: yup.number().nullable(),
        minOvernight: yup.number().nullable(),
        maxOvernight: yup.number().nullable(),
        minTotalGuests: yup.number().nullable(),
        maxTotalGuests: yup.number().nullable(),
        // toleranceRate removed - now stored at hotel level as cancellationToleranceRate
        rate: yup.number().required(),
      }),
    ),
    texts: yup.array().of(
      yup.object().shape({
        details: yup.string().nullable(),
        type: yup.mixed<EContentHotelTextType>().nullable(),
        language: yup.mixed<ELanguageCode>().nullable().oneOf(Object.values(ELanguageCode)),
      }),
    ),
    maxCapacityRooms: yup.number().nullable(),
    maxCapacityPeople: yup.number().nullable(),
    hasFreeParking: yup.boolean().nullable(),
    hasPaidParking: yup.boolean().nullable(),
    freeParkingSpaces: yup
      .number()
      .integer()
      .nullable()
      .label(i18next.t('hotel-settings-attributes-parking-free-spaces'))
      .when('hasFreeParking', {
        is: true,
        then: () => yup.number().integer().required().label(i18next.t('hotel-settings-attributes-parking-free-spaces')),
        otherwise: () => yup.number().integer().nullable().label(i18next.t('hotel-settings-attributes-parking-free-spaces')),
      }),
    paidParkingSpaces: yup
      .number()
      .integer()
      .nullable()
      .label(i18next.t('hotel-settings-attributes-parking-paid-spaces'))
      .when('hasPaidParking', {
        is: true,
        then: () => yup.number().integer().required().label(i18next.t('hotel-settings-attributes-parking-paid-spaces')),
        otherwise: () => yup.number().integer().nullable(),
      }),
  }),
  contacts: yup.array().of(
    yup.object().shape({
      id: yup.number().nullable(),
      firstname: yup.string().nullable(),
      lastname: yup.string().required().label(i18next.t('hotel-contact-lastname')),
      title: yup.string().nullable(),
      job: yup.string().nullable(),
      gender: yup.mixed<EClientGender>().nullable(),
      phone: yup.string().nullable(),
      email: yup.string().nullable(),
      contactType: yup.mixed<EContentHotelContactType>().required().label(i18next.t('hotel-contact-type')),
    }),
  ),
  groupIds: yup.array().of(yup.number()),
});

type HotelFormBlockingTab = { slug: string; label: string; hash?: string };

function hasHotelFormSectionError(section: unknown): boolean {
  if (section == null) return false;
  if (Array.isArray(section)) return section.some(hasHotelFormSectionError);
  if (typeof section === 'object') return Object.keys(section as object).length > 0;
  return true;
}

type HotelFormChildSection = { keys: string[]; hash?: string; labelKey: string; hiddenOnProduction?: boolean };

/** Sections inside a parent tab. Hash tabs match the seminargo-settings CustomTabs; the first four are hidden on production. */
const HOTEL_FORM_CHILD_SECTIONS: Record<string, HotelFormChildSection[]> = {
  notifications: [
    { keys: ['notificationEmail'], labelKey: 'hotel-reminder-receiver-section' },
    { keys: ['reminders'], labelKey: 'hotel-reminder-type-section' },
    {
      keys: ['offerNotApprovedReminderHotel', 'offerNotApprovedReminderClient', 'depositReminderHotel', 'depositReminderCheckHotel'],
      labelKey: 'hotel-reminder-days-section',
    },
  ],
  lister: [
    { keys: ['showInLister'], labelKey: 'hotel-showinlister' },
    {
      keys: ['listerServiceTypeIdSmall', 'listerServiceTypeIdMedium', 'listerServiceTypeIdRegular'],
      labelKey: 'hotel-lister-servicetypes',
    },
    { keys: ['slug', 'rating', 'locationLatitude', 'locationLongitude'], labelKey: 'hotel-lister-settings' },
    { keys: ['texts'], hash: 'texts', labelKey: 'hotel-settings-texts-tab', hiddenOnProduction: true },
    {
      keys: ['meetingRooms', 'maxCapacityRooms', 'maxCapacityPeople'],
      hash: 'capacities',
      labelKey: 'hotel-settings-capacities-tab',
      hiddenOnProduction: true,
    },
    {
      keys: ['stars', 'attributes', 'hasFreeParking', 'hasPaidParking', 'freeParkingSpaces', 'paidParkingSpaces'],
      hash: 'attributes',
      labelKey: 'hotel-settings-attributes-tab',
      hiddenOnProduction: true,
    },
    {
      keys: ['cancellationRules', 'cancellationToleranceRate'],
      hash: 'cancellation',
      labelKey: 'hotel-settings-cancellation-tab',
      hiddenOnProduction: true,
    },
    { keys: ['medias'], hash: 'media', labelKey: 'hotel-settings-media-tab' },
  ],
};

/** Tabs whose yup errors block save (excludes listing/widget status warnings). Epilo nested tabs are named separately. */
function getHotelFormBlockingTabs(
  errors: Record<string, unknown> | undefined,
  options: { hotelId: number; bookingMode: EBookingMode | null },
): HotelFormBlockingTab[] {
  const { hotelId, bookingMode } = options;
  const tabs: Array<{ errorKey: string; slug: string; labelKey: string; visible: boolean }> = [
    { errorKey: 'business', slug: 'general', labelKey: 'hotel-settings-tab', visible: true },
    { errorKey: 'contacts', slug: 'contacts', labelKey: 'hotel-contacts-tab', visible: bookingMode !== EBookingMode.SEM },
    { errorKey: 'notifications', slug: 'notifications', labelKey: 'hotel-notifications-tab', visible: true },
    {
      errorKey: 'lister',
      slug: 'epilo',
      labelKey: 'hotel-lister-tab',
      visible: hotelId > 0 && bookingMode === EBookingMode.EPILO,
    },
    {
      errorKey: 'widget',
      slug: 'sem',
      labelKey: 'hotel-widget-tab',
      visible: hotelId > 0 && bookingMode === EBookingMode.SEM,
    },
  ];

  return tabs.flatMap(tab => {
    if (!tab.visible || !hasHotelFormSectionError(errors?.[tab.errorKey])) return [];

    const parentLabel = String(i18next.t(tab.labelKey));
    const children = HOTEL_FORM_CHILD_SECTIONS[tab.errorKey];
    if (!children) return [{ slug: tab.slug, label: parentLabel }];

    const sectionErrors = (errors?.[tab.errorKey] || {}) as Record<string, unknown>;
    const hideContentTabs = isProductionRelease();
    const childTabs = children
      .filter(child => !(child.hiddenOnProduction && hideContentTabs) && child.keys.some(key => hasHotelFormSectionError(sectionErrors[key])))
      .map(child => ({
        slug: tab.slug,
        hash: child.hash,
        label: `${parentLabel} > ${i18next.t(child.labelKey)}`,
      }));

    return childTabs.length > 0 ? childTabs : [{ slug: tab.slug, label: parentLabel }];
  });
}

function HotelGroupsTab({
  hotelId,
  hotelGroups,
  spaceId,
  onGroupsChange,
  control,
}: {
  hotelId: number;
  hotelGroups: any[];
  spaceId: number;
  onGroupsChange: (groupIds: number[]) => void;
  control: any;
}) {
  const [selectedGroups, setSelectedGroups] = useState<number[]>(hotelGroups.map(g => g.id));
  const [searchTerm, setSearchTerm] = useState('');

  const { data: groupsData, loading } = useQuery(HOTEL_GROUPS_LIST_QUERY, {
    variables: { spaceId: null },
  });

  const handleAddGroup = (groupId: number) => {
    const newSelection = [...selectedGroups, groupId];
    setSelectedGroups(newSelection);
    onGroupsChange(newSelection);
  };

  const handleRemoveGroup = (groupId: number) => {
    const newSelection = selectedGroups.filter(id => id !== groupId);
    setSelectedGroups(newSelection);
    onGroupsChange(newSelection);
  };

  if (loading) return <CircularProgress />;

  const allGroups = groupsData?.listHotelGroups?.hotelGroups || [];
  const assignedGroups = allGroups.filter(g => selectedGroups.includes(g.id));

  const filteredAvailableGroups = searchTerm ? allGroups.filter(g => g.name.toLowerCase().includes(searchTerm.toLowerCase())) : allGroups;

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h5" gutterBottom>
          {i18next.t('hotel-groups-assignment')}
        </Typography>
      </Grid>

      <Grid item xs={12} md={6}>
        <Typography variant="h6" gutterBottom>
          {i18next.t('groups-available')}
        </Typography>
        <TextField
          fullWidth
          variant="outlined"
          placeholder={i18next.t('groups-search') as string}
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          sx={{ mb: 2 }}
        />
        <Paper variant="outlined" sx={{ maxHeight: 400, overflow: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{i18next.t('groups-name')}</TableCell>
                <TableCell align="right">{i18next.t('action')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredAvailableGroups.map(group => (
                <TableRow key={group.id}>
                  <TableCell>{group.name}</TableCell>
                  <TableCell align="right">
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => handleAddGroup(group.id)}
                      disabled={selectedGroups.includes(group.id)}
                      startIcon={<AddIcon />}
                    >
                      {selectedGroups.includes(group.id) ? i18next.t('added') : i18next.t('add')}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {filteredAvailableGroups.length === 0 && (
                <TableRow>
                  <TableCell colSpan={2} align="center">
                    <Typography variant="body2" color="textSecondary">
                      {searchTerm ? i18next.t('no-results') : i18next.t('hotel-groups-none-available')}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      </Grid>

      <Grid item xs={12} md={6}>
        <Typography variant="h6" gutterBottom>
          {i18next.t('groups-assigned')} ({assignedGroups.length})
        </Typography>
        <Box mt={5.5}>
          <Paper variant="outlined" sx={{ maxHeight: 400, overflow: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{i18next.t('groups-name')}</TableCell>
                  <TableCell align="right">{i18next.t('action')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {assignedGroups.map(group => (
                  <TableRow key={group.id}>
                    <TableCell>{group.name}</TableCell>
                    <TableCell align="right">
                      <Button variant="contained" color="error" size="small" onClick={() => handleRemoveGroup(group.id)} startIcon={<DeleteIcon />}>
                        {i18next.t('remove')}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {assignedGroups.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={2} align="center">
                      <Typography variant="body2" color="textSecondary">
                        {i18next.t('hotel-groups-none-assigned')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>
        </Box>
      </Grid>
    </Grid>
  );
}

function HotelForm(props: HotelFormProps) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const bookingMode = bookingModeSelector();

  const [showReminderEditDialog, setShowReminderEditDialog] = useState(false);
  const [showEventTrackingDialog, setShowEventTrackingDialog] = useState(false);
  const [editingReminderIndex, setEditingReminderIndex] = useState<number>(-1);
  const [editingReceivers, setEditingReceivers] = useState<string[]>([]);
  const [newEmail, setNewEmail] = useState('');
  const [isUploadingAGB, setIsUploadingAGB] = useState(false);
  const agbFileInputRef = useRef<HTMLInputElement>(null);
  const isSavingRef = useRef(false);

  // Newly selected media files are kept locally (as a data-URI preview + pending upload) and are
  // only actually uploaded to S3/DB when the hotel form is saved - see uploadPendingMedias().
  const pendingMediaTempIdRef = useRef(0);
  const pendingMediaUploadsRef = useRef<Map<number, { filename: string; mimeType: string; base64: string }>>(new Map());

  const client = useApolloClient();
  const [updateMutateFunction] = useMutation(UPDATE_HOTEL_MUTATION);
  const [createMutateFunction] = useMutation(CREATE_HOTEL_MUTATION);
  const [uploadAGBMutateFunction] = useMutation(UPLOAD_HOTEL_AGB_MUTATION);
  const [uploadMediaMutateFunction] = useMutation(UPLOAD_CONTENTMEDIA_MUTATION);
  const [deleteMediaMutateFunction] = useMutation(DELETE_CONTENTMEDIA_MUTATION);

  // Subscribe to hotel changes from other users
  useSubscription(HOTEL_CHANGED_SUBSCRIPTION, {
    variables: { hotelId: props.data.id > 0 ? props.data.id : null },
    skip: props.data.id <= 0, // Skip for new hotels
    onData: ({ data, client }) => {
      if (data.data?.hotelChanged && data.data.hotelChanged.id === props.data.id) {
        // Skip if we're currently saving (the change is from the current user)
        if (isSavingRef.current) {
          return;
        }

        // Evict cache for this hotel
        client.cache.evict({ fieldName: 'listHotels' });
        client.cache.evict({ fieldName: 'viewHotel', args: { id: props.data.id } });
        client.cache.gc();

        // Notify user
        dispatchMessage(dispatch, i18next.t('hotel-updated-by-another-user'));
      }
    },
    onError: error => {
      console.error('Hotel subscription error:', error);
    },
  });

  const user = userSelector()!;
  const canEdit = props.data.id < 0 || canEditAdminRecord(user, props.data);
  const meetingPackageEnabled = props.data.id > 0 && !!props.data.extRefCode;
  const canEditMeetingPackageData = canEdit && !meetingPackageEnabled;

  const [createdId, setCreatedId] = useState(0);
  useEffect(() => {
    if (createdId > 0) navigate(`/settings/hotels/${createdId}`);
  }, [createdId]);

  type HotelFormType = yup.InferType<typeof validationSchema>;

  const toFormSchema = (obj: HotelListOutput): HotelFormType => ({
    isCreate: props.data.id > 0 ? false : true,
    billing: {
      uid: obj.billingUid,
      iban: obj.billingIban,
      bic: obj.billingBic,
    },
    business: {
      spaceId: obj.space.id,
      name: obj.name,
      refCode: obj.refCode,
      extRefCode: obj.extRefCode,
      businessName: obj.businessName,
      businessAddress1: obj.businessAddress1 || '',
      businessAddress2: obj.businessAddress2,
      businessAddress3: obj.businessAddress3,
      businessCountry: obj.businessCountry,
      businessZip: obj.businessZip,
      businessCity: obj.businessCity,
      businessState: obj.businessState,
      businessEmail: obj.businessEmail,
      businessPhone: obj.businessPhone,
      businessUrl: obj.businessUrl,
    },
    differentInvoiceAddress: !!obj.invoiceAddress,
    invoice: {
      address1: obj.invoiceAddress?.address1,
      address2: obj.invoiceAddress?.address2,
      address4: obj.invoiceAddress?.address4,
      country: obj.invoiceAddress?.country,
      city: obj.invoiceAddress?.city,
      state: obj.invoiceAddress?.state,
      zip: obj.invoiceAddress?.zip,
    },
    notifications: {
      notificationEmail: obj.notificationEmail || '',
      offerNotApprovedReminderHotel: obj.offerNotApprovedReminderHotel || null,
      offerNotApprovedReminderClient: obj.offerNotApprovedReminderClient || null,
      depositReminderHotel: obj.depositReminderHotel || null,
      depositReminderCheckHotel: obj.depositReminderCheckHotel || null,
      reminders: REMINDER_TYPES.map(t => ({
        template: t,
        receivers: obj.reminders?.find(r => r.template === t)?.receivers || [],
      })),
    },
    widget: {
      widgetUrl: obj.widgetUrl || '',
      widgetColor: obj.widgetColor || '',
      widgetColorEnabled: obj.widgetColorEnabled || false,
      widgetPrivacyUrl: obj.widgetPrivacyUrl || '',
      widgetTermsUrl: obj.widgetTermsUrl || '',
      widgetNoRooms: obj.widgetNoRooms || false,
      widgetExpandedMode: obj.widgetExpandedMode || false,
      widgetSlimMode: obj.widgetSlimMode || false,
      offerTemplateId: obj.offerTemplate ? obj.offerTemplate.id : 0,
      offerExpirationDays: obj.offerExpirationDays || null,
      enableAvailabilityCheck: obj.enableAvailabilityCheck || false,
      widgetServiceTypeIds: obj.serviceTypes ? obj.serviceTypes.filter(s => s.forWidget).map(s => s.serviceType.id) : [],
      showInWidget: obj.showInWidget,
    },
    lister: {
      rating: obj.rating,
      slug: obj.slug,
      listerServiceTypeIdSmall:
        (obj.serviceTypes &&
          obj.serviceTypes.find(s => s.forLister && s.listerServiceTypeAssignment === EContentHotelListerServiceTypeAssignment.SMALL)?.serviceType
            ?.id) ||
        0,
      listerServiceTypeIdMedium:
        (obj.serviceTypes &&
          obj.serviceTypes.find(s => s.forLister && s.listerServiceTypeAssignment === EContentHotelListerServiceTypeAssignment.MEDIUM)?.serviceType
            ?.id) ||
        0,
      listerServiceTypeIdRegular:
        (obj.serviceTypes &&
          obj.serviceTypes.find(s => s.forLister && s.listerServiceTypeAssignment === EContentHotelListerServiceTypeAssignment.REGULAR)?.serviceType
            ?.id) ||
        0,
      locationLatitude: obj.locationLatitude ?? undefined,
      locationLongitude: obj.locationLongitude ?? undefined,
      showInLister: obj.showInLister,
      meetingRooms: obj.meetingRooms
        ? obj.meetingRooms.map(mr => ({
          id: mr.id,
          name: mr.name,
          area: mr.area,
          capacityUForm: mr.capacityUForm,
          capacityTheater: mr.capacityTheater,
          capacityParlament: mr.capacityParlament,
          capacityCircle: mr.capacityCircle,
          capacityBankett: mr.capacityBankett,
          capacityCocktail: mr.capacityCocktail,
          capacityBlock: mr.capacityBlock,
          daylight: mr.daylight,
          airConditioning: mr.airConditioning,
          facilityId: mr.facilityId,
        }))
        : [],
      medias: obj.medias,
      stars: obj.attributes?.find(a => a.attribute.startsWith('CATEGORY_'))?.attribute || '',
      attributes:
        obj.attributes?.filter(a => !a.attribute.startsWith('CATEGORY_') && !a.attribute.startsWith('PARKING_')).map(f => f.attribute) || [],
      hasFreeParking: obj.attributes?.some(a => a.attribute === 'PARKING_FREE') || false,
      hasPaidParking: obj.attributes?.some(a => a.attribute === 'PARKING_PAID') || false,
      cancellationToleranceRate:
        obj.cancellationToleranceRate !== null && obj.cancellationToleranceRate !== undefined
          ? Math.round(obj.cancellationToleranceRate * 100)
          : null,
      cancellationRules: obj.cancellationRules?.map(cd => ({
        daysToEvent: cd.daysToEvent,
        minCapacity: cd.minCapacity !== null && cd.minCapacity !== undefined ? Math.round(cd.minCapacity * 100) : null,
        maxCapacity: cd.maxCapacity !== null && cd.maxCapacity !== undefined ? Math.round(cd.maxCapacity * 100) : null,
        minOvernight: cd.minOvernight,
        maxOvernight: cd.maxOvernight,
        minTotalGuests: cd.minTotalGuests,
        maxTotalGuests: cd.maxTotalGuests,
        // toleranceRate removed - now stored at hotel level as cancellationToleranceRate
        rate: Math.round(cd.rate * 100),
      })),
      texts: obj.texts,
      maxCapacityRooms: obj.maxCapacityRooms || null,
      maxCapacityPeople: obj.maxCapacityPeople || null,
      freeParkingSpaces: obj.freeParkingSpaces ?? null,
      paidParkingSpaces: obj.paidParkingSpaces ?? null,
    },
    contacts: obj.contacts?.map(c => ({
      ...c,
      gender: c.gender || ('' as EClientGender),
      contactType: c.contactType || EContentHotelContactType.MAIN_CONTACT,
    })),
    groupIds: obj.groups ? obj.groups.map(g => g.id) : [],
  });

  const formMethods = useForm({
    mode: 'all',
    resolver: yupResolver(validationSchema) as any,
    context: { client: useApolloClient() },
    defaultValues: toFormSchema((props.data || {}) as HotelListOutput),
  });
  const {
    handleSubmit,
    control,
    trigger,
    reset,
    watch,
    getValues,
    setValue,
    formState: { isDirty, isValidating, isSubmitting, errors: validationErrors },
  } = formMethods;

  const blockingTabs = useMemo(
    () => getHotelFormBlockingTabs(validationErrors as Record<string, unknown>, { hotelId: props.data.id, bookingMode }),
    [validationErrors, props.data.id, bookingMode],
  );

  useEffect(() => {
    trigger();
  }, [trigger]);

  const {
    fields: meetingRoomsFields,
    append: meetingRoomsAppend,
    remove: meetingRoomsRemove,
  } = useFieldArray({
    control,
    name: 'lister.meetingRooms',
  });

  const {
    fields: mediasFields,
    append: mediasAppend,
    remove: mediasRemove,
    move: mediasMove,
  } = useFieldArray({
    control,
    name: 'lister.medias',
  });

  const {
    fields: cancellationRulesFields,
    append: cancellationRulesAppend,
    remove: cancellationRulesRemove,
  } = useFieldArray({
    control,
    name: 'lister.cancellationRules',
  });

  // Group rules by category for display
  const cancellationRulesGrouped = useMemo(() => {
    const rules = watch('lister.cancellationRules') || [];

    // Check if we have rules with overnight ranges (Übernachtungsgäste)
    const hasOvernightRules = rules.some(
      (r: any) => (r.minOvernight !== null && r.minOvernight !== undefined) || (r.maxOvernight !== null && r.maxOvernight !== undefined),
    );

    // Check if we have rules with total guests ranges (Seminargäste)
    const hasSeminarRules = rules.some(
      (r: any) => (r.minTotalGuests !== null && r.minTotalGuests !== undefined) || (r.maxTotalGuests !== null && r.maxTotalGuests !== undefined),
    );

    // If both categories exist, separate them
    if (hasOvernightRules && hasSeminarRules) {
      const overnightIndices: number[] = [];
      const seminarIndices: number[] = [];
      const generalIndices: number[] = [];

      rules.forEach((r: any, idx: number) => {
        const hasOvernight = (r.minOvernight !== null && r.minOvernight !== undefined) || (r.maxOvernight !== null && r.maxOvernight !== undefined);
        const hasSeminar =
          (r.minTotalGuests !== null && r.minTotalGuests !== undefined) || (r.maxTotalGuests !== null && r.maxTotalGuests !== undefined);

        if (hasOvernight && !hasSeminar) {
          overnightIndices.push(idx);
        } else if (hasSeminar && !hasOvernight) {
          seminarIndices.push(idx);
        } else {
          generalIndices.push(idx);
        }
      });

      return {
        hasCategories: true,
        overnightIndices,
        seminarIndices,
        generalIndices,
      };
    }

    // No categories - show as general list
    return {
      hasCategories: false,
      overnightIndices: [],
      seminarIndices: [],
      generalIndices: rules.map((_: any, idx: number) => idx),
    };
  }, [watch('lister.cancellationRules')]);

  const {
    fields: textsFields,
    append: textsAppend,
    remove: textsRemove,
  } = useFieldArray({
    control,
    name: 'lister.texts',
  });

  const {
    fields: contactsFields,
    append: contactsAppend,
    remove: contactsRemove,
  } = useFieldArray({
    control,
    name: 'contacts',
  });

  const {
    fields: remindersFields,
    replace: remindersReplace,
    update: remindersUpdate,
  } = useFieldArray({
    control,
    name: 'notifications.reminders',
  });

  const getPreviewParams = () => {
    const formVals = watch();

    const params = [
      `&seminargoColorEnabled=${encodeURIComponent(formVals.widget.widgetColorEnabled)}`,
      `&seminargoColor=${encodeURIComponent(formVals.widget.widgetColor)}`,
      `&seminargoTermsUrl=${encodeURIComponent(formVals.widget.widgetTermsUrl || '')}`,
      `&seminargoPrivacyUrl=${encodeURIComponent(formVals.widget.widgetPrivacyUrl || '')}`,
      `&seminargoNoRooms=${encodeURIComponent(formVals.widget.widgetNoRooms || 'false')}`,
      `&seminargoExpandedMode=${encodeURIComponent(formVals.widget.widgetExpandedMode || 'false')}`,
      `&seminargoSlimMode=${encodeURIComponent(formVals.widget.widgetSlimMode || 'false')}`,
    ];
    return params.join('');
  };

  /**
   * The saved photo will never be larger than this - uploadFile() in seminargo-server-db's s3.ts
   * resizes every image to this max width before storing it.
   */
  const MEDIA_PREVIEW_MAX_WIDTH = 800;

  const resizeImagePreview = (base64: string, mimeType: string, maxWidth: number): Promise<string> =>
    new Promise(resolve => {
      const dataUrl = `data:${mimeType};base64,${base64}`;
      const img = new Image();
      img.onload = () => {
        if (!img.naturalWidth || img.naturalWidth <= maxWidth) {
          resolve(dataUrl);
          return;
        }
        const canvas = document.createElement('canvas');
        canvas.width = maxWidth;
        canvas.height = Math.round((img.naturalHeight * maxWidth) / img.naturalWidth);
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          resolve(dataUrl);
          return;
        }
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL(mimeType));
      };
      img.onerror = () => resolve(dataUrl);
      img.src = dataUrl;
    });

  /**
   * Make a filename unique by inserting a short token before the extension.
   */
  const makeUniqueMediaFilename = (filename: string) => {
    const token = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
    const lastDot = filename.lastIndexOf('.');
    if (lastDot <= 0) return `${filename}-${token}`;
    return `${filename.slice(0, lastDot)}-${token}${filename.slice(lastDot)}`;
  };

  /**
   * Uploads any not-yet-persisted media (pending items added via the "Add Media" picker, tracked
   * with a temporary negative id in pendingMediaUploadsRef) and returns a map of tempId -> real
   * content_Media id, so the hotel save payload can be built with real ids.
   */
  const uploadPendingMedias = async (medias: { id: number }[] | undefined | null, spaceId: number) => {
    const idMap = new Map<number, number>();
    const pendingIds = (medias || []).map(m => m.id).filter(id => id < 0);
    if (pendingIds.length === 0) return idMap;

    const uploaded = await Promise.all(
      pendingIds.map(async tempId => {
        const pending = pendingMediaUploadsRef.current.get(tempId);
        if (!pending) return null;
        const res = await uploadMediaMutateFunction({
          variables: {
            spaceId,
            filename: makeUniqueMediaFilename(pending.filename),
            mimeType: pending.mimeType,
            base64: pending.base64,
            // Hotel Settings -> Seminargo Settings -> Media Selection always tags its uploads SEMINARGO.
            area: EContentMediaArea.SEMINARGO,
          },
          update: cache => EVICT_CONTENTMEDIA_QUERIES(cache),
        });
        return { tempId, id: res.data?.uploadMedia?.id };
      }),
    );
    uploaded.forEach(u => {
      if (u?.id) {
        idMap.set(u.tempId, u.id);
        pendingMediaUploadsRef.current.delete(u.tempId);
      }
    });
    return idMap;
  };

  const onSubmit = async (values: HotelFormType) => {
    isSavingRef.current = true;
    try {
      if (props.data.id > 0) {
        console.log('onSubmit', values.notifications.reminders);
        const mediaIdMap = await uploadPendingMedias(values.lister.medias, values.business.spaceId);
        const resolveMediaId = (id: number) => mediaIdMap.get(id) ?? id;
        const originalMediaIds = new Set((props.data.medias || []).map((m: any) => m.id));
        const removedMediaIds = [...originalMediaIds].filter(
          (id: any) => !(values.lister.medias || []).some((m: any) => resolveMediaId(m.id) === id),
        );
        const res = await updateMutateFunction({
          variables: {
            id: props.data.id,
            data: {
              name: values.business.name,
              billingBic: values.billing.bic || null,
              billingIban: values.billing.iban || null,
              billingUid: values.billing.uid || null,
              businessName: values.business.businessName || null,
              businessAddress1: values.business.businessAddress1 || null,
              businessAddress2: values.business.businessAddress2 || null,
              businessAddress3: values.business.businessAddress3 || null,
              businessAddress4: null,
              businessCountry: values.business.businessCountry,
              businessZip: values.business.businessZip,
              businessCity: values.business.businessCity,
              businessState: values.business.businessState,
              businessEmail: values.business.businessEmail || null,
              businessPhone: values.business.businessPhone || null,
              businessUrl: values.business.businessUrl || null,
              invoiceAddress:
                values.differentInvoiceAddress && values.invoice
                  ? {
                    address1: values.invoice.address1,
                    address2: values.invoice.address2,
                    address3: null,
                    address4: values.invoice.address4,
                    country: values.invoice.country,
                    zip: values.invoice.zip,
                    city: values.invoice.city,
                    state: values.invoice.state,
                  }
                  : null,
              notificationEmail: values.notifications.notificationEmail || null,
              offerNotApprovedReminderHotel: values.notifications.offerNotApprovedReminderHotel || null,
              offerNotApprovedReminderClient: values.notifications.offerNotApprovedReminderClient || null,
              depositReminderHotel: values.notifications.depositReminderHotel || null,
              depositReminderCheckHotel: values.notifications.depositReminderCheckHotel || null,
              reminders: values.notifications.reminders.map(r => ({
                template: r.template,
                receivers: r.receivers,
              })),
              showInWidget: values.widget.showInWidget,
              widgetUrl: values.widget.widgetUrl || null,
              widgetColor: values.widget.widgetColor,
              widgetColorEnabled: values.widget.widgetColorEnabled,
              widgetPrivacyUrl: values.widget.widgetPrivacyUrl || null,
              widgetTermsUrl: values.widget.widgetTermsUrl || null,
              widgetNoRooms: values.widget.widgetNoRooms || false,
              widgetExpandedMode: values.widget.widgetExpandedMode || false,
              widgetSlimMode: values.widget.widgetSlimMode || false,
              offerTemplateId: values.widget.offerTemplateId && values.widget.offerTemplateId > 0 ? values.widget.offerTemplateId : null,
              offerExpirationDays: values.widget.offerExpirationDays || null,
              enableAvailabilityCheck: values.widget.enableAvailabilityCheck || false,
              showInLister: values.lister.showInLister,
              rating: values.lister.rating || null,
              slug: slugify(values.lister.slug || values.business.name || ''),
              widgetServiceTypeIds: values.widget.widgetServiceTypeIds,
              listerServiceTypes: [
                values.lister.listerServiceTypeIdSmall
                  ? { serviceTypeId: values.lister.listerServiceTypeIdSmall, serviceTypeAssignment: EContentHotelListerServiceTypeAssignment.SMALL }
                  : null,
                values.lister.listerServiceTypeIdMedium
                  ? { serviceTypeId: values.lister.listerServiceTypeIdMedium, serviceTypeAssignment: EContentHotelListerServiceTypeAssignment.MEDIUM }
                  : null,
                values.lister.listerServiceTypeIdRegular
                  ? {
                    serviceTypeId: values.lister.listerServiceTypeIdRegular,
                    serviceTypeAssignment: EContentHotelListerServiceTypeAssignment.REGULAR,
                  }
                  : null,
              ]
                .filter(l => l)
                .map(l => l!),
              locationLatitude: values.lister.locationLatitude,
              locationLongitude: values.lister.locationLongitude,
              meetingRooms: values.lister.meetingRooms?.map((mr: HotelMeetingRoomInput) => ({
                id: mr.id,
                name: mr.name,
                area: mr.area,
                capacityUForm: mr.capacityUForm,
                capacityTheater: mr.capacityTheater,
                capacityParlament: mr.capacityParlament,
                capacityCircle: mr.capacityCircle,
                capacityBankett: mr.capacityBankett,
                capacityCocktail: mr.capacityCocktail,
                capacityBlock: mr.capacityBlock,
                daylight: mr.daylight,
                airConditioning: mr.airConditioning,
                facilityId: mr.facilityId,
              })),
              medias: values.lister.medias?.map((m: any, index: number) => {
                return {
                  id: resolveMediaId(m.id),
                  sequence: index,
                  description: m.description || null,
                };
              }),
              attributes: [
                ...(values.lister.attributes || []).map((f: any) => ({
                  attribute: f,
                })),
                values.lister.stars ? { attribute: values.lister.stars! } : null,
                values.lister.hasFreeParking ? { attribute: 'PARKING_FREE' } : null,
                values.lister.hasPaidParking ? { attribute: 'PARKING_PAID' } : null,
              ]
                .filter(a => a)
                .map(a => a!),
              cancellationToleranceRate:
                values.lister.cancellationToleranceRate !== null && values.lister.cancellationToleranceRate !== undefined
                  ? values.lister.cancellationToleranceRate / 100
                  : null,
              cancellationRules:
                values.lister.cancellationRules?.map(cd => ({
                  daysToEvent: cd.daysToEvent,
                  minCapacity: cd.minCapacity !== null && cd.minCapacity !== undefined ? cd.minCapacity / 100 : null,
                  maxCapacity: cd.maxCapacity !== null && cd.maxCapacity !== undefined ? cd.maxCapacity / 100 : null,
                  minOvernight: cd.minOvernight,
                  maxOvernight: cd.maxOvernight,
                  minTotalGuests: cd.minTotalGuests,
                  maxTotalGuests: cd.maxTotalGuests,
                  // toleranceRate removed - now stored at hotel level as cancellationToleranceRate
                  rate: cd.rate / 100,
                })) || [],
              texts: values.lister.texts?.map((t: any) => ({
                details: t.details,
                type: t.type,
                language: t.language,
              })),
              maxCapacityRooms: values.lister.maxCapacityRooms,
              maxCapacityPeople: values.lister.maxCapacityPeople,
              freeParkingSpaces: values.lister.hasFreeParking ? values.lister.freeParkingSpaces : null,
              paidParkingSpaces: values.lister.hasPaidParking ? values.lister.paidParkingSpaces : null,
              contacts: values.contacts?.map(c => ({
                id: c.id,
                firstname: c.firstname || null,
                lastname: c.lastname,
                title: c.title || null,
                job: c.job || null,
                gender: c.gender || null,
                phone: c.phone || null,
                email: c.email || null,
                contactType: c.contactType,
              })),
              groupIds: (values.groupIds || []).filter((id): id is number => id !== undefined),
            },
          },
          update: cache => EVICT_HOTELS_QUERIES(cache),
          awaitRefetchQueries: true,
          refetchQueries: [...REFETCH_HOTELS_QUERIES(props.data.id), { query: LIST_HOTEL_GROUPS_QUERY, variables: { spaceId: null } }],
        });
        if (removedMediaIds.length > 0) {
          // The hotel's own link to these was already removed as part of the update above. deleteMedia (context:
          // SEMINARGO) then either downgrades a BOTH-tagged row to SEM (no delete), or - if it was SEMINARGO-only -
          // actually deletes it for good.
          await Promise.all(
            removedMediaIds.map((id: any) =>
              deleteMediaMutateFunction({
                variables: { id, context: EContentMediaArea.SEMINARGO },
                update: cache => EVICT_CONTENTMEDIA_QUERIES(cache),
              }).catch(() => undefined),
            ),
          );
        }
        reset(toFormSchema((res.data!.updateHotel || {}) as HotelListOutput));
        dispatchMessage(dispatch, i18next.t('hotel-updated'));
      } else {
        const res = await createMutateFunction({
          variables: {
            spaceId: values.business.spaceId,
            data: {
              name: values.business.name,
              billingBic: values.billing.bic || null,
              billingIban: values.billing.iban || null,
              billingUid: values.billing.uid || null,
              businessName: values.business.businessName || null,
              businessAddress1: values.business.businessAddress1 || null,
              businessAddress2: values.business.businessAddress2 || null,
              businessAddress3: values.business.businessAddress3 || null,
              businessAddress4: null,
              businessCountry: values.business.businessCountry,
              businessZip: values.business.businessZip,
              businessCity: values.business.businessCity,
              businessState: values.business.businessState,
              businessEmail: values.business.businessEmail || null,
              businessPhone: values.business.businessPhone || null,
              businessUrl: values.business.businessUrl || null,
              invoiceAddress:
                values.differentInvoiceAddress && values.invoice
                  ? {
                    address1: values.invoice.address1,
                    address2: values.invoice.address2,
                    address3: null,
                    address4: values.invoice.address4,
                    country: values.invoice.country,
                    zip: values.invoice.zip,
                    city: values.invoice.city,
                    state: values.invoice.state,
                  }
                  : null,
              notificationEmail: values.notifications.notificationEmail || null,
              offerNotApprovedReminderHotel: values.notifications.offerNotApprovedReminderHotel || null,
              offerNotApprovedReminderClient: values.notifications.offerNotApprovedReminderClient || null,
              depositReminderHotel: values.notifications.depositReminderHotel || null,
              depositReminderCheckHotel: values.notifications.depositReminderCheckHotel || null,
              showInWidget: values.widget.showInWidget,
              widgetUrl: values.widget.widgetUrl || null,
              widgetColor: values.widget.widgetColor,
              widgetColorEnabled: values.widget.widgetColorEnabled,
              widgetPrivacyUrl: values.widget.widgetPrivacyUrl || null,
              widgetTermsUrl: values.widget.widgetTermsUrl || null,
              widgetNoRooms: values.widget.widgetNoRooms || false,
              widgetExpandedMode: values.widget.widgetExpandedMode || false,
              widgetSlimMode: values.widget.widgetSlimMode || false,
              offerTemplateId: values.widget.offerTemplateId && values.widget.offerTemplateId > 0 ? values.widget.offerTemplateId : null,
              offerExpirationDays: values.widget.offerExpirationDays || null,
              enableAvailabilityCheck: values.widget.enableAvailabilityCheck || false,
              showInLister: values.lister.showInLister,
              rating: values.lister.rating || null,
              contacts: values.contacts?.map(c => ({
                firstname: c.firstname || null,
                lastname: c.lastname,
                title: c.title || null,
                job: c.job || null,
                gender: c.gender || null,
                phone: c.phone || null,
                email: c.email || null,
                contactType: c.contactType,
              })),
              maxCapacityRooms: values.lister.maxCapacityRooms,
              maxCapacityPeople: values.lister.maxCapacityPeople,
              freeParkingSpaces: values.lister.hasFreeParking ? values.lister.freeParkingSpaces : null,
              paidParkingSpaces: values.lister.hasPaidParking ? values.lister.paidParkingSpaces : null,
            },
          },
          update: cache => EVICT_HOTELS_QUERIES(cache),
          awaitRefetchQueries: true,
          refetchQueries: REFETCH_HOTELS_QUERIES(),
        });
        reset(toFormSchema((res.data!.createHotel || {}) as HotelListOutput));
        setCreatedId(res.data!.createHotel.id);
        dispatchMessage(dispatch, i18next.t('hotel-created'));
      }
    } catch (err) {
      dispatchException(dispatch, err);
    } finally {
      // Reset saving flag after a short delay to allow subscription event to be processed
      setTimeout(() => {
        isSavingRef.current = false;
      }, 2000);
    }
  };

  const [coordinatesByAddress] = useLazyQuery(COORDINATES_BY_ADDRESS_QUERY);

  const [loadWordpressUrl, { loading: wordpressUrlLoading }] = useLazyQuery(GET_WORDPRESS_URL_QUERY);

  const handleOpenWordpressPage = async () => {
    const slug = getValues('lister.slug');
    if (!slug) {
      dispatchMessage(dispatch, String(i18next.t('hotel-lister-slug-required')));
      return;
    }

    try {
      const res = await loadWordpressUrl({
        variables: { slug },
        fetchPolicy: 'network-only',
      });
      const url = res.data?.getWordpressUrl;
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        dispatchMessage(dispatch, String(i18next.t('hotel-wordpress-url-not-found')));
      }
    } catch (err) {
      dispatchException(dispatch, err);
    }
  };

  const handleEditReminder = (index: number) => {
    const currentReceivers = getValues(`notifications.reminders.${index}.receivers`);
    setEditingReceivers([...currentReceivers]);
    setEditingReminderIndex(index);
    setNewEmail('');
    setShowReminderEditDialog(true);
  };

  const handleAddEmail = () => {
    if (newEmail.trim() && !editingReceivers.includes(newEmail.trim())) {
      setEditingReceivers([...editingReceivers, newEmail.trim()]);
      setNewEmail('');
    }
  };

  const handleRemoveEmail = (email: string) => {
    setEditingReceivers(editingReceivers.filter(e => e !== email));
  };

  const handleConfirmReminderEdit = () => {
    console.log('handleConfirmReminderEdit', editingReminderIndex, editingReceivers);
    if (editingReminderIndex >= 0) {
      remindersUpdate(editingReminderIndex, {
        template: watch(`notifications.reminders.${editingReminderIndex}.template`),
        receivers: editingReceivers,
      });
    }
    setShowReminderEditDialog(false);
    setEditingReminderIndex(-1);
    setEditingReceivers([]);
    setNewEmail('');
  };

  const handleCancelReminderEdit = () => {
    setShowReminderEditDialog(false);
    setEditingReminderIndex(-1);
    setEditingReceivers([]);
    setNewEmail('');
  };

  const handleAGBFileSelect = async (file: File | undefined) => {
    if (!file || !file.type.includes('pdf')) {
      dispatchException(dispatch, i18next.t('hotel-settings-agb-pdf-required'));
      return;
    }

    setIsUploadingAGB(true);
    try {
      const reader = new FileReader();
      reader.onload = async e => {
        const base64Data = e.target?.result as string;
        try {
          const result = await uploadAGBMutateFunction({
            variables: {
              data: {
                hotelId: props.data.id,
                originalFilename: file.name,
                base64Data,
                mimeType: file.type,
              },
            },
          });

          if (result.data) {
            dispatchMessage(dispatch, i18next.t('hotel-settings-agb-uploaded'));
            // Refetch hotel data
            client.refetchQueries({
              include: [HOTEL_VIEW_QUERY],
            });
          }
        } catch (error: any) {
          dispatchException(dispatch, error.message || i18next.t('hotel-settings-agb-upload-failed'));
        } finally {
          setIsUploadingAGB(false);
          // Reset file input
          if (agbFileInputRef.current) {
            agbFileInputRef.current.value = '';
          }
        }
      };
      reader.readAsDataURL(file);
    } catch (error: any) {
      dispatchException(dispatch, error.message || i18next.t('hotel-settings-agb-file-read-failed'));
      setIsUploadingAGB(false);
    }
  };

  const handleDownloadHotelAGB = async () => {
    try {
      const url = getFullBackendUrl(`/backend/hotels/${props.data.id}/agb/download`);

      // Fetch with credentials to include auth cookies
      const response = await fetch(url, {
        credentials: 'include',
        method: 'GET',
      });

      if (!response.ok) {
        throw new Error(`Download failed with status ${response.status}`);
      }

      // Get filename from Content-Disposition header
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'agb.pdf';
      if (contentDisposition) {
        const matches = contentDisposition.match(/filename[^;=\n]*=(["\']?)([^"\'\n]*)\1/);
        if (matches && matches[2]) {
          filename = matches[2];
        }
      }

      // Create blob and trigger download
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch (error: any) {
      dispatchException(dispatch, error.message || String(i18next.t('hotel-settings-agb-download-failed')));
    }
  };

  return (
    <>
      <Helmet>
        <title>{formatDocumentTitle([i18next.t('hotels-list-page-title'), props.data])}</title>
      </Helmet>
      <FormProvider {...formMethods}>
        <UnsavedChangesPrompt isDirty={isDirty} unsavedPrefix={`/settings/hotels/${props.data.id}`} />

        {/* Event Tracking Dialog: shows sample code for subscribing to widget events */}
        <SimpleDialog
          open={showEventTrackingDialog}
          setOpen={setShowEventTrackingDialog}
          title={i18next.t('hotel-widget-event-tracking-title')}
          maxWidth="md"
          fullWidth
        >
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            {i18next.t('hotel-widget-event-tracking-description')}
          </Typography>
          <InputLabel sx={{ mb: 1 }}>{i18next.t('hotel-widget-event-tracking-snippet-label')}</InputLabel>
          <Box sx={{ position: 'relative', mb: 2 }}>
            <pre style={{ background: '#f5f5f5', padding: 12, borderRadius: 4, overflow: 'auto', fontSize: 12, margin: 0 }}>
              {`<!-- 1) Early stub: erlaubt semWidget.on/ready vor widget.js -->
<script>
  (function(w){w.semWidget=w.semWidget||{on:function(n,l){(w._semWidgetEarlyListeners=w._semWidgetEarlyListeners||[]).push({eventName:n,listener:l})},off:function(){},onAll:function(l){(w._semWidgetEarlyAllListeners=w._semWidgetEarlyAllListeners||[]).push(l)},offAll:function(){},ready:function(c){(w._semWidgetReadyCallbacks=w._semWidgetReadyCallbacks||[]).push(c)}}})(window);
</script>

<!-- 2) Widget einbinden (defer/async erlaubt) -->
<script defer="defer" src="${props.data.widgetBaseUrl!}/widget.js"></script>

<!-- 3) Event Listener registrieren -->
<script>
  window.semWidget.ready(function () {
    // Alle Events abonnieren
    window.semWidget.onAll(function (e) {
      console.log('[SEM]', e.eventName, e.data);

      // Beispiel: Google Analytics / GTM
      // if (window.dataLayer) {
      //   window.dataLayer.push({ event: 'sem_' + e.eventName, ...e.data });
      // }
    });

    // Oder gezielt auf einzelne Events hören
    window.semWidget.on('emailVerification', function (e) {
      if (e.data.verificationStatus === 'successful') {
        // Conversion Tracking
      }
    });
  });
</script>

<div id="seminargo-widget" data-hotel-ref="${props.data.refCode}"></div>`}
            </pre>
            <Tooltip title={i18next.t('hotel-widget-event-tracking-copy')}>
              <IconButton
                size="small"
                sx={{ position: 'absolute', top: 4, right: 4 }}
                onClick={() => {
                  const snippet = `<!-- 1) Early stub: erlaubt semWidget.on/ready vor widget.js -->
<script>
  (function(w){w.semWidget=w.semWidget||{on:function(n,l){(w._semWidgetEarlyListeners=w._semWidgetEarlyListeners||[]).push({eventName:n,listener:l})},off:function(){},onAll:function(l){(w._semWidgetEarlyAllListeners=w._semWidgetEarlyAllListeners||[]).push(l)},offAll:function(){},ready:function(c){(w._semWidgetReadyCallbacks=w._semWidgetReadyCallbacks||[]).push(c)}}})(window);
</script>

<!-- 2) Widget einbinden (defer/async erlaubt) -->
<script defer="defer" src="${props.data.widgetBaseUrl!}/widget.js"></script>

<!-- 3) Event Listener registrieren -->
<script>
  window.semWidget.ready(function () {
    window.semWidget.onAll(function (e) {
      console.log('[SEM]', e.eventName, e.data);
    });
  });
</script>

<div id="seminargo-widget" data-hotel-ref="${props.data.refCode}"></div>`;
                  navigator.clipboard.writeText(snippet);
                  dispatchMessage(dispatch, String(i18next.t('hotel-widget-event-tracking-copied')));
                }}
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <InputLabel sx={{ mb: 1 }}>{i18next.t('hotel-widget-event-tracking-events-label')}</InputLabel>
          <Box sx={{ background: '#f5f5f5', padding: 1.5, borderRadius: 1, fontSize: 13 }}>
            <Typography variant="body2" component="div">
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                <li>
                  <code>widgetLoaded</code>, <code>widgetUnloaded</code> &mdash; Lifecycle
                </li>
                <li>
                  <code>widgetClicked</code>, <code>widgetClickedOnce</code> &mdash; erster Klick
                </li>
                <li>
                  <code>tabClick</code> &mdash; Tab-Wechsel (<code>data.tabName</code>)
                </li>
                <li>
                  <code>viewChange</code> &mdash; View-Wechsel (<code>data.viewName</code>)
                </li>
                <li>
                  <code>nextButtonClick</code> &mdash; Weiter zur Buchungsform
                </li>
                <li>
                  <code>downloadPdfClick</code> &mdash; PDF-Download (mit <code>formState</code>)
                </li>
                <li>
                  <code>emailVerification</code> &mdash; Verifikation (<code>data.verificationStatus</code>: <code>successful</code>/
                  <code>failed</code>)
                </li>
              </ul>
            </Typography>
          </Box>
        </SimpleDialog>

        {/* Reminder Edit Dialog */}
        <SimpleDialog
          open={showReminderEditDialog}
          setOpen={setShowReminderEditDialog}
          title={i18next.t('hotel-reminder-edit-header', {
            type:
              editingReminderIndex >= 0 ? i18next.t(`hotel-reminder-type-${watch(`notifications.reminders.${editingReminderIndex}.template`)}`) : '',
          })}
          customActions={
            <>
              <Button onClick={handleCancelReminderEdit}>{i18next.t('cancel')}</Button>
              <Button onClick={handleConfirmReminderEdit} variant="contained">
                {i18next.t('apply')}
              </Button>
            </>
          }
        >
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                {i18next.t('hotel-reminder-edit-header-helper', { notificationEmail: watch('notifications.notificationEmail') })}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Grid container spacing={1} alignItems="center">
                <Grid item xs>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder={`${i18next.t('hotel-reminder-edit-add-header')}`}
                    value={newEmail}
                    onChange={e => setNewEmail(e.target.value)}
                  />
                </Grid>
                <Grid item>
                  <Button
                    variant="contained"
                    onClick={handleAddEmail}
                    disabled={!newEmail.trim() || editingReceivers.includes(newEmail.trim()) || !yup.string().email().isValidSync(newEmail.trim())}
                  >
                    {i18next.t('add')}
                  </Button>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Grid container spacing={1}>
                {editingReceivers.map((email, index) => (
                  <Grid item key={index}>
                    <Chip label={email} onDelete={() => handleRemoveEmail(email)} color="primary" variant="outlined" />
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </SimpleDialog>

        {meetingPackageEnabled && (
          <Alert
            severity="info"
            icon={<InfoIcon />}
            sx={{ mb: 2 }}
            action={
              <Button color="inherit" size="small" onClick={() => navigate(`/settings/hotels/${props.data.id}/integrations`)}>
                {i18next.t('hotel-mp-open-integrations')}
              </Button>
            }
          >
            {i18next.t('hotel-mp-fields-readonly')}
          </Alert>
        )}

        <CustomTabs
          headers={[
            i18next.t('hotel-settings-tab'),
            i18next.t('hotel-contacts-tab'),
            i18next.t('hotel-notifications-tab'),
            i18next.t('hotel-lister-tab'),
            i18next.t('hotel-widget-tab'),
            i18next.t('hotel-groups-tab'),
            i18next.t('hotel-contracts-tab'),
            i18next.t('hotel-integrations-tab'),
          ]}
          slugs={['general', 'contacts', 'notifications', 'epilo', 'sem', 'groups', 'contracts', 'integrations']}
          icons={[
            hasHotelFormSectionError(validationErrors.business) ? <ErrorIcon /> : undefined,
            hasHotelFormSectionError(validationErrors.contacts) ? <ErrorIcon /> : undefined,
            hasHotelFormSectionError(validationErrors.notifications) ? <ErrorIcon /> : undefined,
            hasHotelFormSectionError(validationErrors.lister) ? (
              <ErrorIcon />
            ) : props.data.id > 0 &&
              props.data.listerStatus !== EHotelListerStatusType.READY &&
              props.data.listerStatus !== EHotelListerStatusType.INACTIVE ? (
              <WarningIcon />
            ) : undefined,
            hasHotelFormSectionError(validationErrors.widget) ? (
              <ErrorIcon />
            ) : props.data.id > 0 &&
              props.data.widgetStatus !== EHotelWidgetStatusType.READY &&
              props.data.widgetStatus !== EHotelWidgetStatusType.INACTIVE ? (
              <WarningIcon />
            ) : undefined,
            undefined, // Contracts tab - no icon needed
            undefined,
          ]}
          hidden={[
            false,
            bookingMode === EBookingMode.SEM,
            false,
            props.data.id < 0 || bookingMode !== EBookingMode.EPILO,
            props.data.id < 0 || bookingMode !== EBookingMode.SEM,
            props.data.id < 0 || bookingMode === EBookingMode.SEM,
            props.data.id < 0 || !user.isSeminargo || bookingMode === EBookingMode.SEM,
            props.data.id < 0,
          ]}
          disabled={[false, false, false, false, false, false, !user.isSeminargo, false]}
          tabs={[
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <FormInputText
                  name="business.name"
                  control={control}
                  label={i18next.t('hotel-name')}
                  disabled={!canEditMeetingPackageData}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormInputText name="business.refCode" control={control} label={i18next.t('hotel-refcode')} disabled />
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormInputText name="business.extRefCode" control={control} label={i18next.t('hotel-extrefcode')} disabled />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Grid container spacing={1}>
                  <Grid item xs={12} sm={6}>
                    {props.data.id > 0 ? (
                      <Box>
                        <InputLabel sx={{ fontSize: '0.75rem', mb: 0.5 }}>{i18next.t('field-space')}</InputLabel>
                        <Chip label={props.data.space.name} onClick={() => navigate(`/settings/spaces/${props.data.space.id}`)} clickable />
                      </Box>
                    ) : (
                      <SpaceSelectionInput
                        checkAdmin
                        useFilterSpaceForRoot
                        name="business.spaceId"
                        control={control}
                        disabled={!canEdit || user.isSingleAdminSpace}
                        required
                      />
                    )}
                  </Grid>
                  <Grid item xs={12} sm={6}></Grid>
                  <Grid item xs={12} sm={6}>
                    <FormInputText
                      name="business.businessEmail"
                      control={control}
                      label={i18next.t('hotel-businessemail')}
                      disabled={!canEditMeetingPackageData}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormInputText
                      name="business.businessPhone"
                      control={control}
                      label={i18next.t('hotel-businessphone')}
                      disabled={!canEditMeetingPackageData}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormInputText name="business.businessUrl" control={control} label={i18next.t('hotel-businessurl')} disabled={!canEdit} />
                  </Grid>
                  <Grid item xs={12}>
                    <FormInputText name="billing.iban" control={control} label={i18next.t('hotel-iban')} disabled={!canEdit || !user.isSeminargo} />
                  </Grid>
                  <Grid item xs={6}>
                    <FormInputText name="billing.uid" control={control} label={i18next.t('hotel-uid')} disabled={!canEdit || !user.isSeminargo} />
                  </Grid>
                  <Grid item xs={6}>
                    <FormInputText name="billing.bic" control={control} label={i18next.t('hotel-bic')} disabled={!canEdit || !user.isSeminargo} />
                  </Grid>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="h6">{i18next.t('hotel-agb-title')}</Typography>
                  </Grid>
                  {props.data.agb && (
                    <Grid item xs={12}>
                      <Paper sx={{ p: 2 }}>
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <Typography variant="subtitle2">{i18next.t('hotel-agb-filename')}:</Typography>
                            <Typography>{props.data.agb.agbFile.originalFilename}</Typography>
                          </Grid>
                          <Grid item xs={12}>
                            <Typography variant="subtitle2">{i18next.t('hotel-agb-filesize')}:</Typography>
                            <Typography>{(props.data.agb.agbFile.fileSize / 1024).toFixed(2)} KB</Typography>
                          </Grid>
                          <Grid item xs={12}>
                            <Typography variant="subtitle2">{i18next.t('hotel-agb-uploaded-at')}:</Typography>
                            <Typography>{new Date(props.data.agb.createdAt).toLocaleDateString()}</Typography>
                          </Grid>
                          <Grid item xs={12}>
                            <Typography variant="subtitle2">{i18next.t('hotel-agb-uploaded-by')}:</Typography>
                            <Typography>{props.data.agb.uploader.name}</Typography>
                          </Grid>
                          <Grid item xs={12}>
                            <Button variant="outlined" onClick={() => handleDownloadHotelAGB()}>
                              {i18next.t('hotel-agb-download')}
                            </Button>
                          </Grid>
                        </Grid>
                      </Paper>
                    </Grid>
                  )}
                  {!props.data.agb && (
                    <Grid item xs={12}>
                      <Alert severity="info">{i18next.t('hotel-agb-empty-info')}</Alert>
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <input
                      type="file"
                      accept=".pdf"
                      onChange={e => handleAGBFileSelect(e.target.files?.[0])}
                      style={{ display: 'none' }}
                      ref={agbFileInputRef}
                    />
                    <Paper
                      sx={{
                        p: 3,
                        border: '2px dashed #ccc',
                        borderRadius: 1,
                        textAlign: 'center',
                        transition: 'all 0.3s ease',
                        backgroundColor: '#fafafa',
                        '&:hover': {
                          borderColor: !canEdit || isUploadingAGB ? '#ccc' : 'primary.main',
                          backgroundColor: !canEdit || isUploadingAGB ? '#fafafa' : '#f5f5f5',
                        },
                      }}
                      onDragOver={e => {
                        if (canEdit && !isUploadingAGB) {
                          e.preventDefault();
                          e.stopPropagation();
                        }
                      }}
                      onDrop={e => {
                        if (canEdit && !isUploadingAGB) {
                          e.preventDefault();
                          e.stopPropagation();
                          const files = e.dataTransfer.files;
                          if (files && files.length > 0) {
                            handleAGBFileSelect(files[0]);
                          }
                        }
                      }}
                    >
                      <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                      <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
                        {i18next.t('hotel-settings-agb-drop-pdf')}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#666', mb: 2 }}>
                        {i18next.t('hotel-settings-agb-or')}
                      </Typography>
                      <Box sx={{ mt: 2 }}>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() => agbFileInputRef.current?.click()}
                          disabled={!canEdit || isUploadingAGB}
                          sx={{ mb: 2 }}
                        >
                          {isUploadingAGB ? i18next.t('hotel-settings-agb-uploading') : i18next.t('hotel-settings-agb-upload-select-file')}
                        </Button>
                      </Box>
                      <Typography variant="caption" sx={{ color: '#999', display: 'block' }}>
                        {i18next.t('hotel-settings-agb-upload-hint')}
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <FormInputText
                      name="business.businessName"
                      control={control}
                      label={i18next.t('hotel-businessname')}
                      disabled={!canEditMeetingPackageData}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormInputText
                      name="business.businessAddress2"
                      control={control}
                      label={i18next.t('hotel-businessaddress2')}
                      disabled={!canEditMeetingPackageData}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormInputText
                      name="business.businessAddress3"
                      control={control}
                      label={i18next.t('hotel-businessaddress3')}
                      disabled={!canEditMeetingPackageData}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormInputText
                      name="business.businessAddress1"
                      control={control}
                      label={i18next.t('hotel-businessaddress1')}
                      disabled={!canEditMeetingPackageData}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <FormInputText
                      name="business.businessZip"
                      control={control}
                      label={i18next.t('hotel-businesszip')}
                      disabled={!canEditMeetingPackageData}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={9}>
                    <FormInputText
                      name="business.businessCity"
                      control={control}
                      label={i18next.t('hotel-businesscity')}
                      disabled={!canEditMeetingPackageData}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormInputCountryDropdown
                      name="business.businessCountry"
                      control={control}
                      label={i18next.t('hotel-businesscountry')}
                      disabled={!canEditMeetingPackageData}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormInputSubdivisionDropdown
                      name="business.businessState"
                      control={control}
                      label={i18next.t('hotel-businessstate')}
                      disabled={!canEditMeetingPackageData}
                      countryFieldName="business.businessCountry"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormInputText
                      name="lister.locationLatitude"
                      control={control}
                      label={i18next.t('hotel-location-latitude')}
                      disabled={!canEditMeetingPackageData}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormInputText
                      name="lister.locationLongitude"
                      control={control}
                      label={i18next.t('hotel-location-longitude')}
                      disabled={!canEditMeetingPackageData}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      color="secondary"
                      variant="contained"
                      sx={{ mr: 1 }}
                      disabled={!canEditMeetingPackageData}
                      onClick={async () => {
                        const res = await coordinatesByAddress({
                          variables: {
                            address: `${getValues('business.businessAddress1')} ${getValues('business.businessAddress2') || ''} ${getValues('business.businessAddress3') || ''} ${getValues('business.businessZip')} ${getValues('business.businessCity')}`,
                          },
                        });
                        if (res.data && res.data.coordinatesByAddress) {
                          const { lat, lng } = res.data.coordinatesByAddress;
                          formMethods.setValue('lister.locationLatitude', lat ?? undefined, { shouldDirty: true });
                          formMethods.setValue('lister.locationLongitude', lng ?? undefined, { shouldDirty: true });
                        }
                      }}
                    >
                      {i18next.t('hotel-location-fetch-coords')}
                    </Button>
                    <Button
                      color="secondary"
                      variant="contained"
                      onClick={() => {
                        const lat = getValues('lister.locationLatitude');
                        const lon = getValues('lister.locationLongitude');
                        window.open(`https://www.google.com/maps/search/?api=1&query=${lat},${lon}`);
                      }}
                    >
                      {i18next.t('hotel-location-openmap')}
                    </Button>
                  </Grid>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                  </Grid>
                </Grid>
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <FormInputCheckbox label={i18next.t('hotel-different-invoice-address')} control={control} name="differentInvoiceAddress" />
                  </Grid>
                  {watch('differentInvoiceAddress') && (
                    <>
                      <Grid item xs={12}>
                        <Typography variant="h6" component="h6" gutterBottom>
                          Rechnungsadresse
                        </Typography>
                        <FormInputText name="invoice.address1" control={control} label={i18next.t('hotel-invoiceaddress1')} disabled={!canEdit} />
                      </Grid>
                      <Grid item xs={12}>
                        <FormInputText name="invoice.address2" control={control} label={i18next.t('hotel-invoiceaddress2')} disabled={!canEdit} />
                      </Grid>
                      <Grid item xs={12}>
                        <FormInputText name="invoice.address4" control={control} label={i18next.t('hotel-invoiceaddress4')} disabled={!canEdit} />
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <FormInputText name="invoice.zip" control={control} label={i18next.t('hotel-businesszip')} disabled={!canEdit} required />
                      </Grid>
                      <Grid item xs={12} sm={9}>
                        <FormInputText name="invoice.city" control={control} label={i18next.t('hotel-businesscity')} disabled={!canEdit} required />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormInputCountryDropdown
                          name="invoice.country"
                          control={control}
                          label={i18next.t('hotel-businesscountry')}
                          disabled={!canEdit}
                          required
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormInputSubdivisionDropdown
                          name="invoice.state"
                          control={control}
                          label={i18next.t('hotel-businessstate')}
                          disabled={!canEdit}
                          countryFieldName="invoice.country"
                        />
                      </Grid>
                    </>
                  )}
                </Grid>
              </Grid>
              {props.data.id > 0 && bookingMode !== EBookingMode.SEM && (
                <Grid item xs={12}>
                  <Box sx={{ mt: 3 }}>
                    <HotelStandardDocuments
                      hotelId={props.data.id}
                      documents={(props.data.documents || []) as any}
                      canEdit={canEdit}
                      isSeminargo={user.isSeminargo}
                      refetch={props.refetch}
                    />
                  </Box>
                </Grid>
              )}
              {/* Hotel Comments Section */}
              {props.data.id > 0 && (user.isSeminargo || user.isRoot) && (
                <Grid item xs={12}>
                  <Box sx={{ mt: 4 }}>
                    <Typography variant="h6" gutterBottom>
                      {i18next.t('hotel-comments-section-title')}
                    </Typography>
                    <HotelCommentsSection hotelId={props.data.id} hotelName={props.data.name} />
                  </Box>
                </Grid>
              )}
              {/* Tasks Section */}
              {props.data.id > 0 && (user.isSeminargo || user.isRoot) && (
                <>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <TasksIcon sx={{ mr: 1 }} />
                      Tasks
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <HotelTasksList hotelId={props.data.id} />
                  </Grid>
                </>
              )}
            </Grid>,
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <SimpleTable
                  name="contacts"
                  headers={[
                    i18next.t('hotel-contact-title'),
                    i18next.t('hotel-contact-firstname'),
                    i18next.t('hotel-contact-lastname'),
                    i18next.t('hotel-contact-gender'),
                    i18next.t('hotel-contact-job'),
                    i18next.t('hotel-contact-phone'),
                    i18next.t('hotel-contact-email'),
                    i18next.t('hotel-contact-type'),
                  ]}
                  rowIds={contactsFields.map((c, index: number) => c.id)}
                  rows={contactsFields.map((c, index: number) => [
                    <FormInputText name={`contacts.${index}.title`} control={control} disabled={!canEdit} />,
                    <FormInputText name={`contacts.${index}.firstname`} control={control} disabled={!canEdit} />,
                    <FormInputText name={`contacts.${index}.lastname`} control={control} disabled={!canEdit} />,
                    <FormInputDropdown
                      name={`contacts.${index}.gender`}
                      control={control}
                      options={Object.keys(EClientGender).map(k => ({
                        value: k,
                        label: i18next.t(`enums-EClientGender-${k}`),
                      }))}
                      disabled={!canEdit}
                    />,
                    <FormInputText name={`contacts.${index}.job`} control={control} disabled={!canEdit} />,
                    <FormInputText name={`contacts.${index}.phone`} control={control} disabled={!canEdit} />,
                    <FormInputText name={`contacts.${index}.email`} control={control} disabled={!canEdit} />,
                    <FormInputDropdown
                      name={`contacts.${index}.contactType`}
                      control={control}
                      options={Object.keys(EContentHotelContactType).map(k => ({
                        value: k,
                        label: i18next.t(`enums-EContentHotelContactType-${k}`),
                      }))}
                      disabled={!canEdit}
                    />,
                    <IconButton onClick={() => contactsRemove(index)}>
                      <DeleteIcon />
                    </IconButton>,
                  ])}
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() =>
                    contactsAppend({
                      lastname: '',
                      gender: '' as EClientGender,
                      contactType: 'MAIN_CONTACT' as EContentHotelContactType,
                    })
                  }
                >
                  {i18next.t('hotel-contact-add')}
                </Button>
              </Grid>
            </Grid>,
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Typography variant="h6">{i18next.t('hotel-reminder-receiver-section')}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <FormInputText
                      name="notifications.notificationEmail"
                      control={control}
                      label={i18next.t('hotel-notificationemail')}
                      disabled={!canEditMeetingPackageData}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    {i18next.t('hotel-notificationemail-helper')}
                  </Grid>
                  {bookingMode === EBookingMode.EPILO && (
                    <>
                      <Grid item xs={12}>
                        <Typography variant="h6">{i18next.t('hotel-reminder-type-section')}</Typography>
                      </Grid>
                      <Grid item xs={12}>
                        {i18next.t('hotel-reminder-type-section-helper')}
                      </Grid>
                      <Grid item xs={12}>
                        <SimpleTable
                          headers={[
                            i18next.t('hotel-reminder-type'),
                            i18next.t('hotel-reminder-description'),
                            i18next.t('hotel-reminder-receiver'),
                            '',
                          ]}
                          rows={remindersFields.map((r, index) => [
                            i18next.t(`hotel-reminder-type-${r.template}`),
                            i18next.t(`hotel-reminder-description-${r.template}`),
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {r.receivers.map((receiver, receiverIndex) => (
                                <Chip key={receiverIndex} label={receiver} size="small" />
                              ))}
                            </Box>,
                            <IconButton onClick={() => handleEditReminder(index)} disabled={!canEdit} size="small">
                              <EditIcon />
                            </IconButton>,
                          ])}
                        />
                      </Grid>
                    </>
                  )}
                </Grid>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Typography variant="h6">{i18next.t('hotel-reminder-days-section')}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormInputNumber
                      name="notifications.offerNotApprovedReminderHotel"
                      control={control}
                      label={i18next.t('hotel-offernotapprovedreminderhotel')}
                      disabled={!canEdit}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormInputNumber
                      name="notifications.offerNotApprovedReminderClient"
                      control={control}
                      label={i18next.t('hotel-offernotapprovedreminderclient')}
                      disabled={!canEdit}
                    />
                  </Grid>
                  {bookingMode === EBookingMode.SEM && (
                    <>
                      <Grid item xs={12} sm={6}>
                        <FormInputNumber
                          name="notifications.depositReminderHotel"
                          control={control}
                          label={i18next.t('hotel-depositreminderhotel')}
                          disabled={!canEdit}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormInputNumber
                          name="notifications.depositReminderCheckHotel"
                          control={control}
                          label={i18next.t('hotel-depositremindercheckhotel')}
                          disabled={!canEdit}
                        />
                      </Grid>
                    </>
                  )}
                </Grid>
              </Grid>
            </Grid>,
            <Grid container spacing={3}>
              {props.data.id > 0 && (
                <Grid item xs={12}>
                  {props.data.listerStatus === EHotelListerStatusType.READY && (
                    <Alert severity="success">{i18next.t('hotel-lister-status-READY')}</Alert>
                  )}
                  {props.data.listerStatus !== EHotelListerStatusType.READY && props.data.listerStatus !== EHotelListerStatusType.INACTIVE && (
                    <Alert severity="warning">
                      {i18next.t(`hotel-lister-status-${props.data.listerStatus}`)}
                      {(props.data.listerStatus === EHotelListerStatusType.PRICELIST_INCOMPLETE ||
                        props.data.listerStatus === EHotelListerStatusType.SERVICE_TYPE_INCOMPLETE) &&
                        props.data.listerValidation && <div style={{ marginTop: '8px' }}>{formatValidationDetails(props.data.listerValidation)}</div>}
                    </Alert>
                  )}
                </Grid>
              )}
              {user.isSeminargo && (
                <>
                  <Grid item xs={12}>
                    <FormInputCheckbox name="lister.showInLister" control={control} label={i18next.t('hotel-showinlister')} disabled={!canEdit} />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="h6">{i18next.t('hotel-lister-servicetypes')}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <FormInputDropdown
                      control={control}
                      name="lister.listerServiceTypeIdSmall"
                      label={i18next.t(`enums-EContentHotelServiceTypeAssignment-SMALL`)}
                      options={buildGroupedSpaceSelectionOptions(
                        user,
                        filterForSelectableRecords(user, props.serviceTypes, getValues('business.spaceId'), false, true).filter(st => st.isSystem),
                        `${i18next.t('hotel-lister-servicetype-add')}`,
                        0,
                      )}
                      disabled={!canEdit}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <FormInputDropdown
                      control={control}
                      name="lister.listerServiceTypeIdMedium"
                      label={i18next.t(`enums-EContentHotelServiceTypeAssignment-MEDIUM`)}
                      options={buildGroupedSpaceSelectionOptions(
                        user,
                        filterForSelectableRecords(user, props.serviceTypes, getValues('business.spaceId'), false, true).filter(st => st.isSystem),
                        `${i18next.t('hotel-lister-servicetype-add')}`,
                        0,
                      )}
                      disabled={!canEdit}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <FormInputDropdown
                      control={control}
                      name="lister.listerServiceTypeIdRegular"
                      label={i18next.t(`enums-EContentHotelServiceTypeAssignment-REGULAR`)}
                      options={buildGroupedSpaceSelectionOptions(
                        user,
                        filterForSelectableRecords(user, props.serviceTypes, getValues('business.spaceId'), false, true).filter(st => st.isSystem),
                        `${i18next.t('hotel-lister-servicetype-add')}`,
                        0,
                      )}
                      disabled={!canEdit}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="h6">{i18next.t('hotel-lister-settings')}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormInputText name="lister.rating" control={control} label={i18next.t('hotel-rating')} disabled={!canEdit} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <FormInputText name="lister.slug" control={control} label={i18next.t('hotel-slug')} disabled={!canEditMeetingPackageData} />
                      <Tooltip title={i18next.t('hotels-list-lister-url')}>
                        <span>
                          <IconButton size="small" onClick={handleOpenWordpressPage} disabled={wordpressUrlLoading}>
                            {wordpressUrlLoading ? <CircularProgress size={18} /> : <OpenInNewIcon fontSize="small" />}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </Grid>
                </>
              )}
              <Grid item xs={12} sm={12}>
                <CustomTabs
                  headers={[
                    i18next.t('hotel-settings-texts-tab'),
                    i18next.t('hotel-settings-capacities-tab'),
                    i18next.t('hotel-settings-attributes-tab'),
                    i18next.t('hotel-settings-cancellation-tab'),
                    i18next.t('hotel-settings-media-tab'),
                    i18next.t('hotel-settings-commission-tab'),
                  ]}
                  slugs={['texts', 'capacities', 'attributes', 'cancellation', 'media', 'commission']}
                  useHash={true}
                  hidden={[...new Array(4).fill(isProductionRelease() ? true : false), false, !user.isSeminargo && !user.isRoot]}
                  tabs={[
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <SimpleTable
                          name="texts"
                          headers={[
                            i18next.t('hotel-settings-texts-type'),
                            i18next.t('hotel-settings-texts-details'),
                            i18next.t('hotel-settings-texts-language'),
                            '',
                          ]}
                          rowIds={textsFields
                            .map((mr, index) => ({ mr, index }))
                            .filter(({ mr }) => !mr.type?.startsWith('MIG_') && mr.type !== 'HOTEL_HEADING' && mr.type !== 'HOTEL_SHORTTEXT')
                            .map(({ mr }) => mr.id)}
                          rows={textsFields
                            .map((mr, index) => ({ mr, index }))
                            .filter(({ mr }) => !mr.type?.startsWith('MIG_') && mr.type !== 'HOTEL_HEADING' && mr.type !== 'HOTEL_SHORTTEXT')
                            .map(({ index }) => [
                              <FormInputDropdown
                                name={`lister.texts.${index}.type`}
                                control={control}
                                options={Object.keys(EContentHotelTextType)
                                  .filter(k => !k.startsWith('MIG_') && k !== 'HOTEL_HEADING' && k !== 'HOTEL_SHORTTEXT')
                                  .map(k => ({
                                    value: k,
                                    label: i18next.t(`enums-EContentHotelTextType-${k}`),
                                  }))}
                                disabled={!canEditMeetingPackageData}
                              />,
                              <FormInputRichText
                                name={`lister.texts.${index}.details`}
                                control={control}
                                options={{
                                  plugins: [''],
                                  toolbar: 'bold italic',
                                  menubar: false,
                                  height: 300,
                                }}
                                disabled={!canEditMeetingPackageData}
                              />,
                              <FormInputLanguageDropdown
                                name={`lister.texts.${index}.language`}
                                control={control}
                                label={i18next.t('hotel-settings-texts-language')}
                                disabled={!canEditMeetingPackageData}
                              />,
                              <IconButton onClick={() => textsRemove(index)} disabled={!canEditMeetingPackageData}>
                                <DeleteIcon />
                              </IconButton>,
                            ])}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Button
                          variant="contained"
                          startIcon={<AddIcon />}
                          disabled={!canEditMeetingPackageData}
                          onClick={() =>
                            textsAppend({
                              details: '',
                              type: EContentHotelTextType.HOTEL_DESCRIPTION,
                              language: ELanguageCode.de,
                            })
                          }
                        >
                          {i18next.t('hotel-settings-texts-add')}
                        </Button>
                      </Grid>
                    </Grid>,
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <Typography variant="h6">{i18next.t('hotel-settings-meeting-rooms')}</Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <SimpleTable
                          name="meetingRooms"
                          headers={[
                            i18next.t('hotel-settings-meeting-rooms-name'),
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', whiteSpace: 'nowrap' }}>
                              <WbSunnyOutlinedIcon sx={{ width: 16, height: 16, mb: 0.5 }} />
                              {i18next.t('hotel-settings-meeting-rooms-daylight')}
                            </Box>,
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', whiteSpace: 'nowrap' }}>
                              <AcUnitOutlinedIcon sx={{ width: 16, height: 16, mb: 0.5 }} />
                              {i18next.t('hotel-settings-meeting-rooms-airconditioning')}
                            </Box>,
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                              {i18next.t('hotel-settings-meeting-rooms-area')}
                              <Typography variant="caption" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                                (m²)
                              </Typography>
                            </Box>,
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                              <CustomIcon name="uform" sx={{ width: 16, height: 16, mb: 0.5 }} />
                              {i18next.t('hotel-settings-meeting-rooms-capacityUForm')}
                              <Tooltip title={String(i18next.t('hotel-meetingroom-capacity-max-tooltip'))} placement="top">
                                <PeopleIcon sx={{ fontSize: '1.2rem', color: 'text.secondary', mt: 0.5 }} />
                              </Tooltip>
                            </Box>,
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                              <CustomIcon name="theater" sx={{ width: 16, height: 16, mb: 0.5 }} />
                              {i18next.t('hotel-settings-meeting-rooms-capacityTheater')}
                              <Tooltip title={String(i18next.t('hotel-meetingroom-capacity-max-tooltip'))} placement="top">
                                <PeopleIcon sx={{ fontSize: '1.2rem', color: 'text.secondary', mt: 0.5 }} />
                              </Tooltip>
                            </Box>,
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                              <CustomIcon name="parlament" sx={{ width: 16, height: 16, mb: 0.5 }} />
                              {i18next.t('hotel-settings-meeting-rooms-capacityParlament')}
                              <Tooltip title={String(i18next.t('hotel-meetingroom-capacity-max-tooltip'))} placement="top">
                                <PeopleIcon sx={{ fontSize: '1.2rem', color: 'text.secondary', mt: 0.5 }} />
                              </Tooltip>
                            </Box>,
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                              <CustomIcon name="kreis" sx={{ width: 16, height: 16, mb: 0.5 }} />
                              {i18next.t('hotel-settings-meeting-rooms-capacityCircle')}
                              <Tooltip title={String(i18next.t('hotel-meetingroom-capacity-max-tooltip'))} placement="top">
                                <PeopleIcon sx={{ fontSize: '1.2rem', color: 'text.secondary', mt: 0.5 }} />
                              </Tooltip>
                            </Box>,
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                              <CustomIcon name="bankett" sx={{ width: 16, height: 16, mb: 0.5 }} />
                              {i18next.t('hotel-settings-meeting-rooms-capacityBankett')}
                              <Tooltip title={String(i18next.t('hotel-meetingroom-capacity-max-tooltip'))} placement="top">
                                <PeopleIcon sx={{ fontSize: '1.2rem', color: 'text.secondary', mt: 0.5 }} />
                              </Tooltip>
                            </Box>,
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                              <CustomIcon name="cocktail" sx={{ width: 16, height: 16, mb: 0.5 }} />
                              {i18next.t('hotel-settings-meeting-rooms-capacityCocktail')}
                              <Tooltip title={String(i18next.t('hotel-meetingroom-capacity-max-tooltip'))} placement="top">
                                <PeopleIcon sx={{ fontSize: '1.2rem', color: 'text.secondary', mt: 0.5 }} />
                              </Tooltip>
                            </Box>,
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                              <CustomIcon name="block" sx={{ width: 16, height: 16, mb: 0.5 }} />
                              {i18next.t('hotel-settings-meeting-rooms-capacityBlock')}
                              <Tooltip title={String(i18next.t('hotel-meetingroom-capacity-max-tooltip'))} placement="top">
                                <PeopleIcon sx={{ fontSize: '1.2rem', color: 'text.secondary', mt: 0.5 }} />
                              </Tooltip>
                            </Box>,
                            '',
                          ]}
                          rowIds={meetingRoomsFields.map((mr, index: number) => mr.id)}
                          rows={meetingRoomsFields.map((mr, index: number) => [
                            <FormInputText name={`lister.meetingRooms.${index}.name`} control={control} disabled={!canEditMeetingPackageData} />,
                            <FormInputCheckbox name={`lister.meetingRooms.${index}.daylight`} control={control} disabled={!canEditMeetingPackageData} />,
                            <FormInputCheckbox
                              name={`lister.meetingRooms.${index}.airConditioning`}
                              control={control}
                              disabled={!canEditMeetingPackageData}
                            />,
                            <FormInputDecimal name={`lister.meetingRooms.${index}.area`} control={control} disabled={!canEditMeetingPackageData} />,
                            <FormInputInteger
                              name={`lister.meetingRooms.${index}.capacityUForm`}
                              control={control}
                              disabled={!canEditMeetingPackageData}
                            />,
                            <FormInputInteger
                              name={`lister.meetingRooms.${index}.capacityTheater`}
                              control={control}
                              disabled={!canEditMeetingPackageData}
                            />,
                            <FormInputInteger
                              name={`lister.meetingRooms.${index}.capacityParlament`}
                              control={control}
                              disabled={!canEditMeetingPackageData}
                            />,
                            <FormInputInteger
                              name={`lister.meetingRooms.${index}.capacityCircle`}
                              control={control}
                              disabled={!canEditMeetingPackageData}
                            />,
                            <FormInputInteger
                              name={`lister.meetingRooms.${index}.capacityBankett`}
                              control={control}
                              disabled={!canEditMeetingPackageData}
                            />,
                            <FormInputInteger
                              name={`lister.meetingRooms.${index}.capacityCocktail`}
                              control={control}
                              disabled={!canEditMeetingPackageData}
                            />,
                            <FormInputInteger
                              name={`lister.meetingRooms.${index}.capacityBlock`}
                              control={control}
                              disabled={!canEditMeetingPackageData}
                            />,
                            <IconButton onClick={() => meetingRoomsRemove(index)} disabled={!canEditMeetingPackageData}>
                              <DeleteIcon />
                            </IconButton>,
                          ])}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Button
                          variant="contained"
                          startIcon={<AddIcon />}
                          disabled={!canEditMeetingPackageData}
                          onClick={() =>
                            meetingRoomsAppend({
                              name: '',
                              area: 0,
                              daylight: false,
                              airConditioning: false,
                            })
                          }
                        >
                          {i18next.t('hotel-settings-meeting-rooms-add')}
                        </Button>
                      </Grid>
                      <Grid item xs={12}>
                        <Divider sx={{ my: 2 }} />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <FormInputNumber
                          name="lister.maxCapacityRooms"
                          control={control}
                          label={i18next.t('hotel-settings-capacities-maxCapacityRooms')}
                          disabled={!canEdit}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <FormInputNumber
                          name="lister.maxCapacityPeople"
                          control={control}
                          label={i18next.t('hotel-settings-capacities-maxCapacityPeople')}
                          disabled={!canEdit}
                        />
                      </Grid>
                    </Grid>,
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <FormInputRadio
                          name="lister.stars"
                          control={control}
                          label={i18next.t(`enums-EContentHotelAttribute-CATEGORY`)}
                          options={[
                            { value: '', label: i18next.t('enums-EContentHotelAttribute-CATEGORY_NONE') },
                            { value: 'CATEGORY_ONE_STARS', label: i18next.t('enums-EContentHotelAttribute-CATEGORY_ONE_STARS') },
                            { value: 'CATEGORY_TWO_STARS', label: i18next.t('enums-EContentHotelAttribute-CATEGORY_TWO_STARS') },
                            { value: 'CATEGORY_THREE_STARS', label: i18next.t('enums-EContentHotelAttribute-CATEGORY_THREE_STARS') },
                            { value: 'CATEGORY_FOUR_STARS', label: i18next.t('enums-EContentHotelAttribute-CATEGORY_FOUR_STARS') },
                            { value: 'CATEGORY_FIVE_STARS', label: i18next.t('enums-EContentHotelAttribute-CATEGORY_FIVE_STARS') },
                            {
                              value: 'CATEGORY_THREE_STARS_SUPERIOR',
                              label: i18next.t('enums-EContentHotelAttribute-CATEGORY_THREE_STARS_SUPERIOR'),
                            },
                            { value: 'CATEGORY_FOUR_STARS_SUPERIOR', label: i18next.t('enums-EContentHotelAttribute-CATEGORY_FOUR_STARS_SUPERIOR') },
                            { value: 'CATEGORY_FIVE_STARS_SUPERIOR', label: i18next.t('enums-EContentHotelAttribute-CATEGORY_FIVE_STARS_SUPERIOR') },
                          ]}
                          disabled={!canEdit}
                        />
                      </Grid>
                      {['HOTELFACILITY', 'ROOM', 'DESIGN', 'ECOLABEL', 'ACTIVITY', 'WELLNESS'].map(f => (
                        <Grid item xs={12} key={f}>
                          <FormInputMultiCheckbox
                            name="lister.attributes"
                            control={control}
                            label={i18next.t(`enums-EContentHotelAttribute-${f}`)}
                            options={Object.keys(EContentHotelAttribute)
                              .filter(k => k.startsWith(f))
                              .map(k => ({
                                value: k,
                                label: i18next.t(`enums-EContentHotelAttribute-${k}`),
                              }))}
                            disabled={!canEdit || (meetingPackageEnabled && ['HOTELFACILITY', 'ROOM', 'ACTIVITY', 'WELLNESS'].includes(f))}
                          />
                        </Grid>
                      ))}
                      <Grid item xs={12}>
                        <Typography variant="subtitle1">{i18next.t('hotel-settings-attributes-parking')}</Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                          <Box sx={{ minWidth: 220 }}>
                            <FormInputCheckbox
                              name="lister.hasFreeParking"
                              control={control}
                              label={i18next.t('hotel-settings-attributes-parking-free')}
                              disabled={!canEdit}
                            />
                            {watch('lister.hasFreeParking') && (
                              <FormInputInteger
                                name="lister.freeParkingSpaces"
                                control={control}
                                label={i18next.t('hotel-settings-attributes-parking-free-spaces')}
                                disabled={!canEdit}
                              />
                            )}
                          </Box>
                          <Box sx={{ minWidth: 220 }}>
                            <FormInputCheckbox
                              name="lister.hasPaidParking"
                              control={control}
                              label={i18next.t('hotel-settings-attributes-parking-paid')}
                              disabled={!canEdit}
                            />
                            {watch('lister.hasPaidParking') && (
                              <FormInputInteger
                                name="lister.paidParkingSpaces"
                                control={control}
                                label={i18next.t('hotel-settings-attributes-parking-paid-spaces')}
                                disabled={!canEdit}
                              />
                            )}
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>,
                    <Grid container spacing={3}>
                      {!props.data.hasActivePartnerContract && (
                        <>
                          <Grid item xs={12}>
                            {i18next.t('hotel-cancellation-details-nopartner')}
                          </Grid>
                        </>
                      )}
                      {props.data.hasActivePartnerContract && (
                        <>
                          <Grid item xs={12}>
                            {i18next.t('hotel-cancellation-details-partner')}
                          </Grid>
                        </>
                      )}
                      <Grid item xs={12}>
                        <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
                          <Grid item>
                            <InputLabel>{i18next.t('hotel-cancellation-agreed-waiver')}</InputLabel>
                          </Grid>
                          <Grid item>
                            <Tooltip title={i18next.t('hotel-cancellation-agreed-waiver-info')}>
                              <InfoIcon fontSize="small" color="action" />
                            </Tooltip>
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <FormInputPercentage name="lister.cancellationToleranceRate" control={control} disabled={!canEdit} label="" />
                          </Grid>
                        </Grid>
                        <SimpleTable
                          name="cancellationRules"
                          headers={[
                            i18next.t('hotel-cancellation-details-cancellation-days'),
                            //i18next.t('hotel-cancellation-details-cancellation-capacity'),
                            i18next.t('hotel-cancellation-details-cancellation-overnight'),
                            i18next.t('hotel-cancellation-details-cancellation-totalguests'),
                            i18next.t('hotel-cancellation-details-rate'),
                            '',
                          ]}
                          rowIds={cancellationRulesFields.map((mr, index: number) => mr.id)}
                          rows={cancellationRulesFields.map((mr, index: number) => [
                            <Grid container spacing={1}>
                              <Grid item xs={12}>
                                <FormInputNumber
                                  required
                                  name={`lister.cancellationRules.${index}.daysToEvent`}
                                  control={control}
                                  disabled={!canEdit}
                                />
                              </Grid>
                            </Grid>,
                            //<Grid container spacing={1}>
                            //  <Grid item xs={6}><FormInputPercentage name={`lister.cancellationRules.${index}.minCapacity`} control={control} disabled={!canEdit} /></Grid>
                            //  <Grid item xs={6}><FormInputPercentage name={`lister.cancellationRules.${index}.maxCapacity`} control={control} disabled={!canEdit} /></Grid>
                            //</Grid>,
                            <Grid container spacing={1}>
                              <Grid item xs={6}>
                                <FormInputNumber name={`lister.cancellationRules.${index}.minOvernight`} control={control} disabled={!canEdit} />
                              </Grid>
                              <Grid item xs={6}>
                                <FormInputNumber name={`lister.cancellationRules.${index}.maxOvernight`} control={control} disabled={!canEdit} />
                              </Grid>
                            </Grid>,
                            <Grid container spacing={1}>
                              <Grid item xs={6}>
                                <FormInputNumber name={`lister.cancellationRules.${index}.minTotalGuests`} control={control} disabled={!canEdit} />
                              </Grid>
                              <Grid item xs={6}>
                                <FormInputNumber name={`lister.cancellationRules.${index}.maxTotalGuests`} control={control} disabled={!canEdit} />
                              </Grid>
                            </Grid>,
                            <FormInputPercentage required name={`lister.cancellationRules.${index}.rate`} control={control} disabled={!canEdit} />,
                            <IconButton onClick={() => cancellationRulesRemove(index)}>
                              <DeleteIcon />
                            </IconButton>,
                          ])}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Button
                          variant="contained"
                          color="secondary"
                          startIcon={<AddIcon />}
                          onClick={() => {
                            cancellationRulesAppend({ daysToEvent: 30, rate: 0 });
                          }}
                        >
                          {i18next.t('hotel-cancellation-details-add')}
                        </Button>
                        {(watch('lister.cancellationRules')?.length || 0) === 0 && (
                          <Button
                            variant="contained"
                            color="secondary"
                            startIcon={<ContentCopyIcon />}
                            onClick={() => {
                              listDefaultClientCancellationRules(watch('business.businessCountry')).forEach(cr => cancellationRulesAppend(cr));
                            }}
                          >
                            {i18next.t('hotel-cancellation-details-add-defaults')}
                          </Button>
                        )}
                      </Grid>
                      <Grid item xs={12}>
                        {i18next.t('hotel-cancellation-details-defaultrules-premiumclient')}
                      </Grid>
                      <Grid item xs={12}>
                        <SimpleTable
                          name="defaultCancellationRules"
                          headers={[
                            i18next.t('hotel-cancellation-details-cancellation-days'),
                            //i18next.t('hotel-cancellation-details-cancellation-capacity'),
                            i18next.t('hotel-cancellation-details-cancellation-overnight'),
                            i18next.t('hotel-cancellation-details-cancellation-totalguests'),
                            i18next.t('hotel-cancellation-details-tolerance'),
                            i18next.t('hotel-cancellation-details-rate'),
                          ]}
                          rows={listPremiumClientCancellationRules(watch('business.businessCountry')).map((cr, index: number) => [
                            <>{cr.daysToEvent}</>,
                            //<>{cr.minCapacity ? formatPercentage(cr.minCapacity, 0) : '*'} - {cr.maxCapacity ? formatPercentage(cr.maxCapacity, 0) : '*'}</>,
                            <>* - *</>,
                            <>* - *</>,
                            <>{cr.toleranceRate && formatPercentage(cr.toleranceRate, 0)}</>,
                            <>{formatPercentage(cr.rate, 0)}</>,
                          ])}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        {i18next.t('hotel-cancellation-details-defaultrules-defaultclient')}
                      </Grid>
                      <Grid item xs={12}>
                        <SimpleTable
                          name="defaultCancellationRules"
                          headers={[
                            i18next.t('hotel-cancellation-details-cancellation-days'),
                            //i18next.t('hotel-cancellation-details-cancellation-capacity'),
                            i18next.t('hotel-cancellation-details-cancellation-overnight'),
                            i18next.t('hotel-cancellation-details-cancellation-totalguests'),
                            i18next.t('hotel-cancellation-details-tolerance'),
                            i18next.t('hotel-cancellation-details-rate'),
                          ]}
                          rows={listDefaultClientCancellationRules(watch('business.businessCountry')).map((cr, index: number) => [
                            <>{cr.daysToEvent}</>,
                            //<>{cr.minCapacity ? formatPercentage(cr.minCapacity, 0) : '*'} - {cr.maxCapacity ? formatPercentage(cr.maxCapacity, 0) : '*'}</>,
                            <>* - *</>,
                            <>* - *</>,
                            <>{cr.toleranceRate && formatPercentage(cr.toleranceRate, 0)}</>,
                            <>{formatPercentage(cr.rate, 0)}</>,
                          ])}
                        />
                      </Grid>
                    </Grid>,
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <SortableMediaTable
                          medias={mediasFields.map((mr: any) => ({
                            id: mr.id,
                            name: mr.name,
                            previewUrl: mr.previewUrl,
                            logo: mr.logo,
                            offer: mr.offer,
                            outdoorView: mr.outdoorView,
                            restaurant: mr.restaurant,
                            room: mr.room,
                            seminarRoom: mr.seminarRoom,
                            homepage: mr.homepage,
                          }))}
                          headers={[
                            i18next.t('hotel-settings-medias-name'),
                            '',
                            i18next.t('hotel-settings-medias-description'),
                            '',
                            '',
                          ]}
                          onReorder={(startIndex: number, endIndex: number) => {
                            mediasMove(startIndex, endIndex);
                          }}
                          disabled={!canEditMeetingPackageData}
                          onRemove={mediasRemove}
                          renderDescription={(index: number) => (
                            <FormInputText
                              name={`lister.medias.${index}.description`}
                              control={control}
                              label={i18next.t('hotel-settings-medias-description')}
                              disabled={!canEditMeetingPackageData}
                              inputProps={{ maxLength: 60 }}
                              helperText={`${(watch(`lister.medias.${index}.description`) || '').length}/60`}
                            />
                          )}
                          renderActions={(index: number) => (
                            <IconButton onClick={() => mediasRemove(index)} disabled={!canEditMeetingPackageData}>
                              <DeleteIcon />
                            </IconButton>
                          )}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <FileUploadButton
                          name="addHotelMedia"
                          multiple
                          accept="image/*"
                          disabled={!canEditMeetingPackageData}
                          startIcon={<AddIcon />}
                          binary={() => true}
                          onChange={async (name, type, content) => {
                            if (mediasFields.some(mr => mr.name === name)) return;
                            pendingMediaTempIdRef.current -= 1;
                            const tempId = pendingMediaTempIdRef.current;
                            pendingMediaUploadsRef.current.set(tempId, { filename: name, mimeType: type, base64: content });
                            const previewUrl = await resizeImagePreview(content, type, MEDIA_PREVIEW_MAX_WIDTH);
                            mediasAppend({
                              id: tempId,
                              name,
                              previewUrl,
                              description: null,
                            });
                          }}
                        >
                          {i18next.t('hotel-settings-medias-add')}
                        </FileUploadButton>
                      </Grid>
                    </Grid>,
                    <HotelCommissionTab hotelId={props.data.id} />,
                  ]}
                />
              </Grid>
            </Grid>,
            <Grid container spacing={3}>
              {props.data.id > 0 && (
                <Grid item xs={12}>
                  {props.data.widgetStatus === EHotelWidgetStatusType.READY && (
                    <Alert severity="success">{i18next.t('hotel-widget-status-READY')}</Alert>
                  )}
                  {props.data.widgetStatus !== EHotelWidgetStatusType.READY && props.data.widgetStatus !== EHotelWidgetStatusType.INACTIVE && (
                    <Alert severity="warning">
                      {i18next.t(`hotel-widget-status-${props.data.widgetStatus}`)}
                      {(props.data.widgetStatus === EHotelWidgetStatusType.PRICELIST_INCOMPLETE ||
                        props.data.widgetStatus === EHotelWidgetStatusType.SERVICE_TYPE_INCOMPLETE) &&
                        props.data.widgetValidation && <div style={{ marginTop: '8px' }}>{formatValidationDetails(props.data.widgetValidation)}</div>}
                    </Alert>
                  )}
                </Grid>
              )}
              <Grid item xs={12}>
                <FormInputCheckbox name="widget.showInWidget" control={control} label={i18next.t('hotel-showinwidget')} disabled={!canEdit} />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="h6">{i18next.t('hotel-servicetypes-widget')}</Typography>
              </Grid>
              <Grid item xs={12}>
                <GroupedSpaceMultiCheckboxInput
                  name="widget.widgetServiceTypeIds"
                  list={filterForSelectableRecords(user, props.serviceTypes, getValues('business.spaceId'), false, true).filter(st => !st.isSystem)}
                  control={control}
                  mapElement={s => <Link to={`/servicetypes/${s.id}`}>{s.name}</Link>}
                  disabled={!canEdit}
                />
              </Grid>
              <Grid item xs={12}>
                <CustomTabs
                  headers={[i18next.t('hotel-sem-widget-settings'), i18next.t('hotel-sem-offer-settings')]}
                  hidden={[false, false]}
                  tabs={[
                    <Grid container spacing={3}>
                      <Grid item xs={12}></Grid>
                      <Grid item xs={12} sm={3}>
                        <FormInputCheckbox name="widget.widgetColorEnabled" control={control} label={i18next.t('hotel-widget-color-enabled')} />
                        <FormColorPicker
                          name="widget.widgetColor"
                          control={control}
                          label={i18next.t('hotel-widget-color')}
                          required={watch('widget.widgetColorEnabled')}
                          helperText={i18next.t('hotel-widget-color-helper')}
                          disabled={!canEdit || !watch('widget.widgetColorEnabled')}
                        />
                      </Grid>
                      <Grid item xs={12} sm={2}></Grid>
                      <Grid item xs={12} sm={7}>
                        <InputLabel>{i18next.t('hotel-widget-code')}</InputLabel>
                        <pre>
                          {`<script defer="defer" src="${props.data.widgetBaseUrl!}/widget.js"></script>`}
                          <br />
                          {`<div id="seminargo-widget" data-hotel-ref="${props.data.refCode}"></div>`}
                        </pre>
                        <Button size="small" variant="text" onClick={() => setShowEventTrackingDialog(true)} sx={{ textTransform: 'none', pl: 0 }}>
                          {i18next.t('hotel-widget-advanced-options')}
                        </Button>
                      </Grid>
                      <Grid item xs={12}>
                        <Divider />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormInputText
                          name="widget.widgetPrivacyUrl"
                          control={control}
                          label={i18next.t('hotel-widget-privacy-url')}
                          helperText={i18next.t('hotel-widget-privacy-url-helper')}
                          disabled={!canEdit}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormInputText
                          name="widget.widgetTermsUrl"
                          control={control}
                          label={i18next.t('hotel-widget-terms-url')}
                          helperText={i18next.t('hotel-widget-terms-url-helper')}
                          disabled={!canEdit}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Divider />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <SimpleDropdown
                          name="widget.widgetMode"
                          label={i18next.t('hotel-widget-display-mode')}
                          disabled={!canEdit}
                          value={watch('widget.widgetExpandedMode') ? 'expanded' : 'slim'}
                          options={[
                            { value: 'slim', label: i18next.t('enums-EHotelWidgetModeType-SLIM') },
                            { value: 'expanded', label: i18next.t('enums-EHotelWidgetModeType-EXPANDED') },
                          ]}
                          onChange={async v => {
                            formMethods.setValue('widget.widgetExpandedMode', v === 'expanded', {
                              shouldValidate: true,
                              shouldDirty: true,
                              shouldTouch: true,
                            });
                            formMethods.setValue('widget.widgetSlimMode', v === 'slim', {
                              shouldValidate: true,
                              shouldDirty: true,
                              shouldTouch: true,
                            });
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Divider />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormInputCheckbox
                          name="widget.widgetNoRooms"
                          control={control}
                          label={i18next.t('hotel-widget-no-rooms')}
                          helperText={i18next.t('hotel-widget-no-rooms-helper')}
                          disabled={!canEdit}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Divider />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormInputText
                          name="widget.widgetUrl"
                          control={control}
                          label={i18next.t('hotel-widgeturl')}
                          helperText={i18next.t('hotel-widgeturl-helper')}
                          disabled={!canEdit}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Divider />
                      </Grid>
                      {props.data.id > 0 && watch('widget.showInWidget') && (
                        <Grid item xs={12}>
                          <Typography variant="h6">{i18next.t('hotel-widget-preview')}</Typography>
                          <iframe
                            src={`${props.data.widgetTestUrl!}${getPreviewParams()}`}
                            style={{ width: '100%', height: '800px', border: 'none' }}
                          />
                        </Grid>
                      )}
                    </Grid>,
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={4}>
                        <GroupedSpaceSelectionInput
                          name="widget.offerTemplateId"
                          control={control}
                          label={i18next.t('hotel-offertemplate')}
                          list={filterForSelectableRecords(user, props.offerTemplates, getValues('business.spaceId'), false, true)}
                          disabled={!canEdit}
                        />
                      </Grid>
                      <Grid item xs={12} sm={2}>
                        <FormInputNumber
                          name="widget.offerExpirationDays"
                          control={control}
                          label={i18next.t('hotel-offerexpirationdays')}
                          disabled={!canEdit}
                        />
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <FormInputCheckbox
                          name="widget.enableAvailabilityCheck"
                          control={control}
                          label={i18next.t('hotel-enableavailabilitycheck')}
                          disabled={!canEdit}
                        />
                      </Grid>
                    </Grid>,
                  ]}
                />
              </Grid>
            </Grid>,
            <HotelGroupsTab
              hotelId={props.data.id}
              hotelGroups={props.data.groups || []}
              spaceId={props.data.space.id}
              onGroupsChange={groupIds => setValue('groupIds', groupIds, { shouldDirty: true })}
              control={control}
            />,
            <ContractsTab hotelId={props.data.id} canEdit={canEdit} />,
            <HotelMeetingPackageTab
              hotelId={props.data.id}
              spaceId={props.data.space.id}
              propertyIdentifier={props.data.extRefCode}
              hotelName={props.data.name}
            />,
          ]}
        />
      </FormProvider>
      {canEdit && (
        <Grid item xs={12}>
          {blockingTabs.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {i18next.t('hotel-validation-error', {
                tabs: blockingTabs.map(tab => tab.label).join(', '),
                interpolation: { escapeValue: false },
              })}
            </Alert>
          )}
          <Button
            sx={{ marginRight: 2 }}
            variant="contained"
            startIcon={isSubmitting ? <CircularProgress size={24} /> : <SaveIcon />}
            disabled={(props.data.id > 0 && !isDirty) || isSubmitting || isValidating}
            onClick={async () => {
              const valid = await trigger();
              if (valid) {
                handleSubmit(onSubmit)();
                return;
              }

              const invalidTabs = getHotelFormBlockingTabs(formMethods.formState.errors as Record<string, unknown>, {
                hotelId: props.data.id,
                bookingMode,
              });
              const tabNames = invalidTabs.map(tab => tab.label).join(', ');
              dispatchException(dispatch, i18next.t('hotel-validation-error', { tabs: tabNames, interpolation: { escapeValue: false } }));

              const firstTab = invalidTabs[0];
              if (firstTab?.slug) {
                const hotelPathId = props.data.id > 0 ? props.data.id : '_create';
                const hash = firstTab.hash ? `#${firstTab.hash}` : '';
                navigate(`/settings/hotels/${hotelPathId}/${firstTab.slug}${hash}`);
              }
            }}
          >
            {i18next.t('hotel-save')}
          </Button>
          {props.data.id > 0 && <HotelDeleteButton id={props.data.id} spaceId={props.data.space.id} icon={false} />}
        </Grid>
      )}
    </>
  );
}

/**
 * Hotel detail page wrapper that loads hotel data.
 * Renders hotel form for editing hotel information, settings, and configurations.
 * Supports filtering by booking mode.
 */
export default function Hotel(props: HotelProps) {
  const filter = filterSelector();
  const bookingMode = bookingModeSelector();
  const hotelQuery = useQuery(HOTEL_VIEW_QUERY, {
    variables: { id: props.id },
  });
  const hotelSpaceId = hotelQuery.data?.viewHotel?.space?.id ?? null;

  const serviceTypesQuery = useQuery(SERVICETYPES_LIST_QUERY, {
    variables: { spaceId: hotelSpaceId },
    skip: !hotelQuery.data?.viewHotel,
  });
  const offerTemplatesQuery = useQuery(OFFERTEMPLATES_LIST_QUERY);

  const loading = hotelQuery.loading || serviceTypesQuery.loading || offerTemplatesQuery.loading;
  const error = hotelQuery.error || serviceTypesQuery.error || offerTemplatesQuery.error;

  if (loading) return <CircularProgress />;
  else if (!loading && error) return <RedirectError err={error} />;
  else
    return (
      <HotelForm
        data={hotelQuery.data!.viewHotel!}
        serviceTypes={serviceTypesQuery.data!.listServiceTypes.serviceTypes}
        offerTemplates={offerTemplatesQuery.data!.listOfferTemplates}
        refetch={hotelQuery.refetch}
      />
    );
}

/**
 * Hotel creation page for new hotels.
 */
export function HotelCreate(props: HotelCreateProps) {
  const filter = filterSelector();
  const bookingMode = bookingModeSelector();
  const user = userSelector()!;

  const serviceTypesQuery = useQuery(SERVICETYPES_LIST_QUERY, {
    variables: { spaceId: filter?.spaceId || null },
  });
  const offerTemplatesQuery = useQuery(OFFERTEMPLATES_LIST_QUERY);

  const loading = serviceTypesQuery.loading || offerTemplatesQuery.loading;
  const error = serviceTypesQuery.error || offerTemplatesQuery.error;

  if (loading) return <CircularProgress />;
  else if (!loading && error) return <RedirectError err={error} />;
  else
    return (
      <HotelForm
        data={
          {
            id: -1,
            name: '',
            refCode: '',
            businessCountry: ECountryCode.AT,
            widgetColor: '#000000',
            widgetExpandedMode: true,
            widgetServiceTypeIds: [],
            listerServiceTypes: [],
            showInLister: false,
            showInWidget: false,
            space: { id: initialSpaceId(user, filter) ?? filter?.spaceId },
          } as any
        }
        serviceTypes={serviceTypesQuery.data!.listServiceTypes.serviceTypes}
        offerTemplates={offerTemplatesQuery.data!.listOfferTemplates}
        refetch={() => { }}
      />
    );
}

interface HotelDeleteButtonProps {
  id: number;
  spaceId: number;
  icon: boolean;
}
export function HotelDeleteButton(props: HotelDeleteButtonProps) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const user = userSelector()!;
  const canEdit = user.isSeminargo;

  const [deleteMutateFunction, { loading: deleteMutateLoading }] = useMutation(DELETE_HOTEL_MUTATION);

  if (!canEdit) return null;

  return (
    <ConfirmationButton
      sx={{ marginRight: 2 }}
      disabled={!canEdit || deleteMutateLoading}
      icon={props.icon}
      {...(props.icon
        ? {}
        : {
          startIcon: deleteMutateLoading ? <CircularProgress size={24} /> : <DeleteIcon />,
          variant: 'contained',
          color: 'secondary',
        })}
      confirmationQuestion={i18next.t('hotel-confirm-delete')}
      confirmationTitle={i18next.t('hotel-confirm-delete-title')}
      onConfirm={async () => {
        if (!canEdit) return;
        try {
          const res = await deleteMutateFunction({
            variables: {
              id: props.id,
            },
            update: cache => EVICT_HOTELS_QUERIES(cache),
            awaitRefetchQueries: true,
            refetchQueries: REFETCH_HOTELS_QUERIES(),
          });
          navigate('/settings/hotels');
          dispatchMessage(dispatch, i18next.t('hotel-deleted'));
        } catch (err) {
          dispatchException(dispatch, err);
        }
      }}
    >
      {props.icon && (deleteMutateLoading ? <CircularProgress size={24} /> : <DeleteIcon />)}
      {!props.icon && i18next.t('hotel-delete')}
    </ConfirmationButton>
  );
}
